import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Map, 
  Cuboid, 
  Zap, 
  DollarSign, 
  Activity, 
  FileCheck, 
  Send, 
  Radar,
  Mic,
  MicOff,
  Volume2,
  Shield,
  Coins,
  Compass,
  Crosshair,
  Globe,
  Sparkles,
  CheckCircle,
  AlertCircle,
  Terminal,
  Play,
  Pause,
  ArrowRight,
  Sparkle,
  Trash2,
  Search,
  SlidersHorizontal,
  RefreshCw,
  Layers,
  Download,
  VolumeX,
  Keyboard,
  Plus
} from 'lucide-react';

export const SpectacleCommandTerminal: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'PROPOSAL' | 'TELEMETRY' | 'VOICE_CONSOLE' | 'HOTKEYS'>(() => {
    return (localStorage.getItem('nexorax_spectacle_active_tab') as any) || 'PROPOSAL';
  });
  
  // Standard telemetry states
  const [venue, setVenue] = useState(() => {
    return localStorage.getItem('nexorax_spectacle_venue') || '';
  });
  const [budget, setBudget] = useState(() => {
    return localStorage.getItem('nexorax_spectacle_budget') || '';
  });
  const [fleetSize, setFleetSize] = useState(() => {
    return localStorage.getItem('nexorax_spectacle_fleet_size') || '100';
  });
  const [pyroLevel, setPyroLevel] = useState(() => {
    return localStorage.getItem('nexorax_spectacle_pyro_level') || 'STANDARD';
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [proposalReady, setProposalReady] = useState(false);
  const [telemetry, setTelemetry] = useState({ alt: 120, bat: 98 });

  // Voice Console Integration state parameters
  const [micState, setMicState] = useState<'IDLE' | 'LISTENING' | 'BLOCKED' | 'GRANTED'>('IDLE');
  const [micPermissionGranted, setMicPermissionGranted] = useState<boolean | null>(null);
  const [spokenTranscript, setSpokenTranscript] = useState<string>('');
  const [activeVoiceDome, setActiveVoiceDome] = useState<boolean>(false);
  const [orbitMultiplier, setOrbitMultiplier] = useState<number>(1);
  const [lastActionNode, setLastActionNode] = useState<string>('AWAITING ACTIVATION COMMANDS');
  const [hasVoiceRecognition, setHasVoiceRecognition] = useState<boolean>(true);
  
  // New States: Voice Sensitivity & Demo Loop
  const [voiceSensitivity, setVoiceSensitivity] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'MAX'>(() => {
    return (localStorage.getItem('nexorax_spectacle_voice_sensitivity') as any) || 'MEDIUM';
  });
  const [demoLoopActive, setDemoLoopActive] = useState<boolean>(false);
  const [demoLoopStep, setDemoLoopStep] = useState<number>(0);
  const [demoLoopSpeedSeconds, setDemoLoopSpeedSeconds] = useState<number>(4);
  const [logSearchQuery, setLogSearchQuery] = useState<string>('');
  const [logTypeFilter, setLogTypeFilter] = useState<'ALL' | 'VOICE' | 'MANUAL' | 'DEMO' | 'SYSTEM'>('ALL');
  const [isVoiceMuted, setIsVoiceMuted] = useState<boolean>(() => {
    return localStorage.getItem('nexorax_spectacle_is_voice_muted') === 'true';
  });
  const [simulatingVoiceWave, setSimulatingVoiceWave] = useState<boolean>(false);

  // MAPPED HOTKEY REGISTRATION CONSOLE PARAMS
  const [hkAction, setHkAction] = useState<string>('TOGGLE_MARKETPLACE_VIEW');
  const [hkKey, setHkKey] = useState<string>('m');
  const [hkCtrl, setHkCtrl] = useState<boolean>(false);
  const [hkAlt, setHkAlt] = useState<boolean>(true);
  const [hkShift, setHkShift] = useState<boolean>(false);
  const [hkMeta, setHkMeta] = useState<boolean>(false);
  const [hkName, setHkName] = useState<string>('Toggle NexoraX Marketplace View');
  const [hkDescription, setHkDescription] = useState<string>('Instantly opens or closes the NexoraX Marketplace modal/view from any visual screen coordinate to accelerate navigation.');
  const [isRecordingHk, setIsRecordingHk] = useState<boolean>(false);
  
  const [hotkeyManagerList, setHotkeyManagerList] = useState<any[]>(() => {
    try {
      const cached = localStorage.getItem('nexorax_NexoraX_hotkeys');
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {
      console.error(e);
    }
    return [];
  });

  // Hotkey recording effect
  useEffect(() => {
    if (!isRecordingHk) return;

    function captureStroke(e: KeyboardEvent) {
      e.preventDefault();
      const rawKey = e.key;
      // Ignore alone modifier taps
      if (['Control', 'Alt', 'Shift', 'Meta'].includes(rawKey)) return;

      setHkKey(rawKey);
      setHkCtrl(e.ctrlKey);
      setHkAlt(e.altKey);
      setHkShift(e.shiftKey);
      setHkMeta(e.metaKey);
      setIsRecordingHk(false);
    }

    window.addEventListener('keydown', captureStroke, true);
    return () => window.removeEventListener('keydown', captureStroke, true);
  }, [isRecordingHk]);

  // HOTKEYS DIRECTORY MATRIX DEFINITION & HELPERS
  const ACTION_OPTIONS = [
    { action: 'TOGGLE_MARKETPLACE_VIEW', name: 'Toggle NexoraX Marketplace View', desc: 'Instantly opens or closes the NexoraX Marketplace modal/view from any visual screen coordinate to accelerate navigation.' },
    { action: 'TOGGLE_MARKETPLACE_ADMIN', name: 'Toggle Marketplace Admin Portal', desc: 'Enters or exits the inline product override editing console inside NexoraX Marketplace.' },
    { action: 'TOGGLE_DETAIL_MODE', name: 'Toggle Product Detailed View', desc: 'Toggles between high-fidelity 3D projector detail mode and list presentation view.' },
    { action: 'ADD_TO_LEDGER', name: 'Trigger NexoraX Handshake', desc: 'Increments Shopping Ledger cache (Cart) by one node for the currently highlighted asset.' },
    { action: 'CLEAR_LEDGER_CART', name: 'Reset Shopping Ledger', desc: 'Purges shopping ledger cache, resetting node count instantly.' },
    { action: 'CYCLE_GEOMETRY', name: 'Cycle 3D Wireframe Geometry', desc: 'Cycles active interactive PDP visualizer geometry through concentric rings, cube, sphere, or torus.' },
    { action: 'SELECT_RANDOM_ASSET', name: 'Select Random Asset', desc: 'Selects a random product from the 50 high-fidelity premium assets list.' },
    { action: 'FILTER_ALL', name: 'Filter: ALL segments', desc: 'Clears selective pipeline isolation, restoring view to all categories.' },
    { action: 'FILTER_INFRASTRUCTURE', name: 'Filter: Infrastructure', desc: 'Filters products list downstream to only Infrastructure assets.' },
    { action: 'FILTER_COMMUNICATIONS', name: 'Filter: Communications', desc: 'Filters products list downstream to only Communications assets.' },
    { action: 'FILTER_NEURAL', name: 'Filter: Neural Core', desc: 'Filters products downstream to only Neural assets.' },
    { action: 'FILTER_WORKSPACE', name: 'Filter: Spatial Workspace', desc: 'Filters products downstream to only Workspace assets.' },
    { action: 'FILTER_SECURITY', name: 'Filter: Cryptographic Security', desc: 'Filters products downstream to only Security assets.' },
    { action: 'NEXT_PAGE', name: 'Navigate: Next Page Segment', desc: 'Advances list to the next page segment.' },
    { action: 'PREV_PAGE', name: 'Navigate: Previous Page Segment', desc: 'Recedes list to the previous page segment.' },
    { action: 'GOTO_SPECTACLE_COMMAND', name: 'Navigate: Spectacle Command Node', desc: 'Instantly transitions active view to the Spectacle Command Node.' },
    { action: 'TOGGLE_HOTKEY_HELP', name: 'Toggle Keyboard Matrix Guide', desc: 'Instantly opens the Global Keyboard Shortcut Reference Directory.' }
  ];

  const getMergedHotkeys = () => {
    const defaultList = [
      { id: 'toggle-marketplace', name: 'Toggle NexoraX Marketplace View', action: 'TOGGLE_MARKETPLACE_VIEW', key: 'm', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false, description: 'Instantly opens or closes the NexoraX Marketplace modal/view from any visual screen coordinate to accelerate navigation.' },
      { id: 'toggle-admin', name: 'Toggle Marketplace Admin Portal', action: 'TOGGLE_MARKETPLACE_ADMIN', key: 'a', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false, description: 'Enters or exits the inline product override editing console.' },
      { id: 'toggle-detail', name: 'Toggle Product Detailed View', action: 'TOGGLE_DETAIL_MODE', key: 'd', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false, description: 'Toggles between high-fidelity 3D projector detail mode and list presentation view.' },
      { id: 'add-cart', name: 'Trigger NexoraX Handshake', action: 'ADD_TO_LEDGER', key: 'h', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false, description: 'Increments Shopping Ledger cache (Cart) by one node for the currently highlighted asset.' },
      { id: 'clear-cart', name: 'Reset Shopping Ledger', action: 'CLEAR_LEDGER_CART', key: 'c', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false, description: 'Purges shopping ledger cache, resetting node count instantly.' },
      { id: 'cycle-geometry', name: 'Cycle 3D Wireframe Geometry', action: 'CYCLE_GEOMETRY', key: 'g', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false, description: 'Cycles active interactive PDP visualizer geometry.' },
      { id: 'random-asset', name: 'Select Random Asset', action: 'SELECT_RANDOM_ASSET', key: 'r', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false, description: 'Selects a random product from the assets list.' },
      { id: 'filter-all', name: 'Filter: ALL segments', action: 'FILTER_ALL', key: '0', ctrlKey: true, altKey: false, shiftKey: false, metaKey: false, description: 'Clears selective pipeline isolation, restoring view to all categories.' },
      { id: 'filter-infra', name: 'Filter: Infrastructure', action: 'FILTER_INFRASTRUCTURE', key: '1', ctrlKey: true, altKey: false, shiftKey: false, metaKey: false, description: 'Filters products list downstream to only Infrastructure assets.' },
      { id: 'filter-comms', name: 'Filter: Communications', action: 'FILTER_COMMUNICATIONS', key: '2', ctrlKey: true, altKey: false, shiftKey: false, metaKey: false, description: 'Filters products list downstream to only Communications assets.' },
      { id: 'filter-neural', name: 'Filter: Neural Core', action: 'FILTER_NEURAL', key: '3', ctrlKey: true, altKey: false, shiftKey: false, metaKey: false, description: 'Filters products downstream to only Neural assets.' },
      { id: 'filter-workspace', name: 'Filter: Spatial Workspace', action: 'FILTER_WORKSPACE', key: '4', ctrlKey: true, altKey: false, shiftKey: false, metaKey: false, description: 'Filters products downstream to only Workspace assets.' },
      { id: 'filter-security', name: 'Filter: Cryptographic Security', action: 'FILTER_SECURITY', key: '5', ctrlKey: true, altKey: false, shiftKey: false, metaKey: false, description: 'Filters products downstream to only Security assets.' },
      { id: 'next-page', name: 'Navigate: Next Page Segment', action: 'NEXT_PAGE', key: 'ArrowRight', ctrlKey: false, altKey: false, shiftKey: false, metaKey: false, description: 'Advances list to the next page segment.' },
      { id: 'prev-page', name: 'Navigate: Previous Page Segment', action: 'PREV_PAGE', key: 'ArrowLeft', ctrlKey: false, altKey: false, shiftKey: false, metaKey: false, description: 'Recedes list to the previous page segment.' },
      { id: 'goto-spectacle', name: 'Navigate: Spectacle Command Node', action: 'GOTO_SPECTACLE_COMMAND', key: 's', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false, description: 'Instantly transitions the active view dashboard to the Spectacle Command Node.' },
      { id: 'toggle-help', name: 'Toggle Keyboard Matrix Guide', action: 'TOGGLE_HOTKEY_HELP', key: 'k', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false, description: 'Instantly opens the Global Keyboard Shortcut Reference Directory.' }
    ];

    const list = [...hotkeyManagerList];
    defaultList.forEach(defHk => {
      if (!list.find(h => h.action === defHk.action)) {
        list.push(defHk);
      }
    });
    return list;
  };

  const getPrettyKeyString = (hk: { ctrlKey: boolean; altKey: boolean; shiftKey: boolean; metaKey: boolean; key: string }) => {
    const parts = [];
    if (hk.ctrlKey) parts.push('Ctrl');
    if (hk.altKey) parts.push('Alt');
    if (hk.shiftKey) parts.push('Shift');
    if (hk.metaKey) parts.push('Meta');
    
    let keyL = hk.key;
    if (keyL === ' ') keyL = 'Space';
    else if (keyL.length === 1) keyL = keyL.toUpperCase();
    parts.push(keyL);
    return parts.join(' + ');
  }

  const getConflictHk = () => {
    const allBound = getMergedHotkeys();
    const conflict = allBound.find(h => 
      h.key.toLowerCase() === hkKey.toLowerCase() &&
      h.ctrlKey === hkCtrl &&
      h.altKey === hkAlt &&
      h.shiftKey === hkShift &&
      h.metaKey === hkMeta &&
      h.action !== hkAction
    );
    return conflict;
  };

  const handleRegisterHk = () => {
    const currentList = getMergedHotkeys();
    const updated = currentList.map(h => {
      if (h.action === hkAction) {
        return {
          ...h,
          key: hkKey,
          ctrlKey: hkCtrl,
          altKey: hkAlt,
          shiftKey: hkShift,
          metaKey: hkMeta,
          name: hkName,
          description: hkDescription
        };
      }
      return h;
    });

    if (!currentList.some(h => h.action === hkAction)) {
      updated.push({
        id: `hk-${Date.now()}`,
        name: hkName,
        action: hkAction,
        key: hkKey,
        ctrlKey: hkCtrl,
        altKey: hkAlt,
        shiftKey: hkShift,
        metaKey: hkMeta,
        description: hkDescription
      });
    }

    localStorage.setItem('nexorax_NexoraX_hotkeys', JSON.stringify(updated));
    setHotkeyManagerList(updated);
    
    window.dispatchEvent(new CustomEvent('nexorax-hotkeys-reloaded', { detail: updated }));
    triggerSoundTone(850, 1100, 0.15);
  };

  const handleDeleteHotkey = (act: string) => {
    const currentList = getMergedHotkeys();
    // Revert back block or remove custom mapping from the saved stack
    const updated = currentList.filter(h => h.action !== act);
    
    localStorage.setItem('nexorax_NexoraX_hotkeys', JSON.stringify(updated));
    setHotkeyManagerList(updated);
    window.dispatchEvent(new CustomEvent('nexorax-hotkeys-reloaded', { detail: updated }));
    triggerSoundTone(450, 300, 0.15);
  };

  const handleResetDefaults = () => {
    localStorage.removeItem('nexorax_NexoraX_hotkeys');
    setHotkeyManagerList([]);
    window.dispatchEvent(new CustomEvent('nexorax-hotkeys-reloaded', { detail: [] }));
    triggerSoundTone(600, 900, 0.2);
  };

  useEffect(() => {
    localStorage.setItem('nexorax_spectacle_active_tab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem('nexorax_spectacle_venue', venue);
  }, [venue]);

  useEffect(() => {
    localStorage.setItem('nexorax_spectacle_budget', budget);
  }, [budget]);

  useEffect(() => {
    localStorage.setItem('nexorax_spectacle_fleet_size', fleetSize);
  }, [fleetSize]);

  useEffect(() => {
    localStorage.setItem('nexorax_spectacle_pyro_level', pyroLevel);
  }, [pyroLevel]);

  useEffect(() => {
    localStorage.setItem('nexorax_spectacle_voice_sensitivity', voiceSensitivity);
  }, [voiceSensitivity]);

  useEffect(() => {
    localStorage.setItem('nexorax_spectacle_is_voice_muted', String(isVoiceMuted));
  }, [isVoiceMuted]);

  // Predefined neural/brain cinematic videos
  const bgVideos = ["/brain1.mp4", "/brain2.mp4", "/brain4.mp4", "/brain9.mp4", "/brain10.mp4", "/words1.mp4"];

  const [localOpacity, setLocalOpacity] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_video_opacity');
      return saved !== null ? parseFloat(saved) : 0.65;
    }
    return 0.65;
  });

  const [localBrightness, setLocalBrightness] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_video_brightness');
      if (saved === null || saved === '1.2' || saved === '0.35' || saved === '0.15') {
        localStorage.setItem('nexorax_video_brightness', '0.08');
        return 0.08;
      }
      return parseFloat(saved);
    }
    return 0.08;
  });

  const [localAutoDim, setLocalAutoDim] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('nexorax_video_autodim') === 'true';
    }
    return false;
  });

  const [localBlur, setLocalBlur] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('nexorax_video_blur') === 'true';
    }
    return false;
  });

  const [localVideoIndex, setLocalVideoIndex] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const savedSrc = localStorage.getItem('nexorax_current_video_src') || '/brain1.mp4';
      const index = ["/brain1.mp4", "/brain2.mp4", "/brain4.mp4", "/brain9.mp4", "/brain10.mp4", "/words1.mp4"].indexOf(savedSrc);
      return index !== -1 ? index : 0;
    }
    return 0;
  });

  const [localAutoplay, setLocalAutoplay] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_video_autoplay');
      return saved !== 'false';
    }
    return true;
  });

  const [localPaused, setLocalPaused] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_video_paused');
      return saved === 'true';
    }
    return false;
  });

  const [localTransitionDuration, setLocalTransitionDuration] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_video_transition_duration');
      return saved !== null ? parseInt(saved, 10) : 1000;
    }
    return 1000;
  });

  useEffect(() => {
    const handleOpacityGlobal = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setLocalOpacity(customEvent.detail);
      }
    };
    const handleBrightnessGlobal = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setLocalBrightness(customEvent.detail);
      }
    };
    const handleBlurGlobal = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setLocalBlur(customEvent.detail);
      }
    };
    const handleCycleGlobal = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (typeof customEvent.detail === 'string') {
        const index = bgVideos.indexOf(customEvent.detail);
        if (index !== -1) {
          setLocalVideoIndex(index);
        }
      }
    };
    const handleAutoplayGlobal = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setLocalAutoplay(customEvent.detail);
      }
    };
    const handlePlayPauseGlobal = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setLocalPaused(customEvent.detail);
      }
    };
    const handleTransitionDurationGlobal = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setLocalTransitionDuration(customEvent.detail);
      }
    };
    const handleAutoDimToggleGlobal = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setLocalAutoDim(customEvent.detail);
      }
    };

    window.addEventListener('nexorax-video-opacity', handleOpacityGlobal);
    window.addEventListener('nexorax-video-brightness', handleBrightnessGlobal);
    window.addEventListener('nexorax-video-blur', handleBlurGlobal);
    window.addEventListener('nexorax-video-cycle', handleCycleGlobal);
    window.addEventListener('nexorax-video-autoplay', handleAutoplayGlobal);
    window.addEventListener('nexorax-video-playpause', handlePlayPauseGlobal);
    window.addEventListener('nexorax-video-transition-duration', handleTransitionDurationGlobal);
    window.addEventListener('nexorax-video-autodim-toggle', handleAutoDimToggleGlobal);

    return () => {
      window.removeEventListener('nexorax-video-opacity', handleOpacityGlobal);
      window.removeEventListener('nexorax-video-brightness', handleBrightnessGlobal);
      window.removeEventListener('nexorax-video-blur', handleBlurGlobal);
      window.removeEventListener('nexorax-video-cycle', handleCycleGlobal);
      window.removeEventListener('nexorax-video-autoplay', handleAutoplayGlobal);
      window.removeEventListener('nexorax-video-playpause', handlePlayPauseGlobal);
      window.removeEventListener('nexorax-video-transition-duration', handleTransitionDurationGlobal);
      window.removeEventListener('nexorax-video-autodim-toggle', handleAutoDimToggleGlobal);
    };
  }, []);

  const triggerSoundToneRef = useRef(triggerSoundTone);
  useEffect(() => {
    triggerSoundToneRef.current = triggerSoundTone;
  }, [triggerSoundTone]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl + Alt + ArrowUp / ArrowDown
      if (e.ctrlKey && e.altKey) {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setLocalBrightness((prev) => {
            const next = Math.min(2.0, Math.round((prev + 0.1) * 100) / 100);
            window.dispatchEvent(new CustomEvent('nexorax-video-brightness', { detail: next }));
            localStorage.setItem('nexorax_video_brightness', String(next));
            triggerSoundToneRef.current(600 + next * 200, 80, 0.05);
            return next;
          });
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          setLocalBrightness((prev) => {
            const next = Math.max(0.05, Math.round((prev - 0.1) * 100) / 100);
            window.dispatchEvent(new CustomEvent('nexorax-video-brightness', { detail: next }));
            localStorage.setItem('nexorax_video_brightness', String(next));
            triggerSoundToneRef.current(600 + next * 200, 80, 0.05);
            return next;
          });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Wave Simulation Helper state buffers
  const [simulatedAmplitude, setSimulatedAmplitude] = useState<number[]>(Array(15).fill(10));
  const [liveDb, setLiveDb] = useState<number>(-80);
  const [liveHz, setLiveHz] = useState<number>(0);
  const [activeSignalMatch, setActiveSignalMatch] = useState<boolean>(false);

  // Web Audio operational references
  const micStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number | null>(null);
  
  // Fireworks bursts indicators (rendered directly inside telemetry mapping area)
  const [pyroParticles, setPyroParticles] = useState<Array<{ id: number; x: number; y: number; color: string }>>([]);

  // Live voice terminal registries (connected directly mapped to nodes)
  interface VoiceRouteLog {
    id: string;
    phrase: string;
    routeNode: string;
    handshake: string;
    time: string;
    triggerType: 'VOICE' | 'MANUAL' | 'DEMO' | 'SYSTEM';
    sensitivityValue: string;
    latencyMs: number;
  }

  const [voiceRouteLogs, setVoiceRouteLogs] = useState<Array<VoiceRouteLog>>([
    { 
      id: '1', 
      phrase: 'SYSTEM STACK DEPLOYED', 
      routeNode: 'Node-21 (Command Ledger)', 
      handshake: '0.8s SUCCESS', 
      time: new Date().toLocaleTimeString(),
      triggerType: 'SYSTEM',
      sensitivityValue: 'AUTO',
      latencyMs: 800
    }
  ]);

  // Command Registry Mapping Schema
  const VOICE_COMMANDS_REGISTRY = [
    { trigger: 'LAUNCH SWARM / TAKE OFF', action: 'ARM FLIGHT PATH & ASCEND TELEMETRY TO 350M', nodeLink: 'Node-09.2 (Antigravity Swarm)' },
    { trigger: 'LAND DRONES / ABORT', action: 'GRADUAL DESCENT & POWER FLEET MOTORS TO ZERO', nodeLink: 'Node-17 (Chronos Ledger)' },
    { trigger: 'IGNITE PYRO / FIREWORKS', action: 'DISCHARGE LOCALIZED COORDINATES EXPLOSIVE VISUALS', nodeLink: 'Node-16 (The Gavel)' },
    { trigger: 'MATRIX SPIN / SPIN', action: 'ACCELERATE Swarm ROTATION VELOCITY FACTOR (x3.5)', nodeLink: 'Node-06 (The Oracle)' },
    { trigger: 'ACTIVATE SHIELD / SHIELD ON', action: 'ENGAGE ORBITAL ELECTROMAGNETIC DEFENSIVE SPHERE', nodeLink: 'Node-01 (Vanguard-Closer)' },
    { trigger: 'DIRECTOR ROUTE / NAVIGATE HOME', action: 'SYNC SWARM COORDINATES DIRECTLY TO DUNFERMLINE HQ', nodeLink: 'NexoraX Core HQ' }
  ];

  // Dynamic Event Stream telemetry updates
  useEffect(() => {
    if (proposalReady) {
      const interval = setInterval(() => {
        setTelemetry(prev => ({
          alt: Math.random() > 0.85 ? Math.floor(501 + Math.random() * 50) : prev.alt > 0 ? Math.floor(prev.alt + Math.random() * 10 - 5) : 0,
          bat: Math.max(0, prev.bat - (Math.random() > 0.8 ? 1 : 0))
        }));
      }, 800);
      return () => clearInterval(interval);
    }
  }, [proposalReady]);

  // Audio waveform continuous animation
  const getSensitivityGateDb = (level: 'LOW' | 'MEDIUM' | 'HIGH' | 'MAX') => {
    switch (level) {
      case 'LOW': return -22;
      case 'MEDIUM': return -38;
      case 'HIGH': return -52;
      case 'MAX': return -68;
    }
  };

  const stopMicrophoneStream = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
    if (audioContextRef.current) {
      try {
        if (audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close();
        }
      } catch (e) {}
      audioContextRef.current = null;
    }
    setMicState('IDLE');
    setLiveDb(-80);
    setLiveHz(0);
    setActiveSignalMatch(false);
  };

  // Audio waveform continuous animation / analysis
  useEffect(() => {
    if (micState === 'LISTENING') return;

    // Elegant ambient sinewave generation for idle or disconnected states
    const updateAmbientWave = () => {
      if (simulatingVoiceWave) {
        // High random wave peaks to simulate a spoken command in real-time
        setSimulatedAmplitude(() => {
          return Array.from({ length: 15 }, () => {
            const pct = 40 + Math.floor(Math.random() * 55);
            return Math.min(100, Math.max(20, pct));
          });
        });
        // Boost parameters above normal thresholds to trigger match visuals!
        setLiveDb(Math.round((-14 + Math.random() * 8) * 10) / 10);
        setLiveHz(Math.floor(220 + Math.random() * 120));
        setActiveSignalMatch(true);
      } else {
        setSimulatedAmplitude(() => {
          return Array.from({ length: 15 }, (_, idx) => {
            const time = Date.now() * 0.0035;
            const sine = Math.sin(time + idx * 0.45);
            const base = 8 + Math.abs(sine) * 22;
            return Math.max(5, Math.floor(base));
          });
        });
        setLiveDb(-80);
        setLiveHz(0);
        setActiveSignalMatch(false);
      }
    };
    const interval = setInterval(updateAmbientWave, 80);
    return () => clearInterval(interval);
  }, [micState, simulatingVoiceWave]);

  // Request native microphone access handler & start voice capture
  const initiateMicrophoneStream = async () => {
    if (micState === 'LISTENING') {
      stopMicrophoneStream();
      triggerSoundTone(400, 200, 0.15);
      return;
    }

    try {
      setMicState('IDLE');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      setMicPermissionGranted(true);
      setMicState('LISTENING');
      triggerSoundTone(600, 1000, 0.15);

      // Setup raw browser sound analysis node
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioCtxClass) {
        const audioCtx = new AudioCtxClass();
        audioContextRef.current = audioCtx;

        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 64; // Gives 32 frequency bins
        analyser.smoothingTimeConstant = 0.65;
        source.connect(analyser);
        analyserRef.current = analyser;

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const updateRealAudio = () => {
          if (analyserRef.current && micStreamRef.current && micStreamRef.current.active) {
            analyserRef.current.getByteFrequencyData(dataArray);

            const sampled: number[] = [];
            let totalVal = 0;
            let peakVal = 0;
            let peakIndex = 0;

            for (let i = 0; i < 15; i++) {
              const dataIdx = Math.floor((i / 15) * bufferLength);
              const byteVal = dataArray[dataIdx] || 0;
              const pct = 6 + (byteVal / 255) * 94;
              sampled.push(Math.round(pct));

              totalVal += byteVal;
              if (byteVal > peakVal) {
                peakVal = byteVal;
                peakIndex = dataIdx;
              }
            }
            setSimulatedAmplitude(sampled);

            // Calculate live dBs (average power across the frequency spectra)
            const average = totalVal / 15;
            const computedDb = -85 + (average / 255) * 85;
            setLiveDb(Math.round(computedDb * 10) / 10);

            // Compute peak frequency in Hertz
            const sampleRate = audioCtx.sampleRate || 44100;
            const hzStep = sampleRate / analyser.fftSize;
            const hz = Math.round(peakIndex * hzStep);
            setLiveHz(peakVal > 15 ? hz : 0);

            // Volume activity detection checks against current dynamic sensitivity barrier
            const sensitivityGate = getSensitivityGateDb(voiceSensitivity);
            const isNexoraXActive = computedDb > sensitivityGate && peakVal > 18;
            setActiveSignalMatch(isNexoraXActive);

            animationRef.current = requestAnimationFrame(updateRealAudio);
          }
        };
        animationRef.current = requestAnimationFrame(updateRealAudio);
      }
    } catch (err) {
      console.warn("Speech permission denied or unsupported environment:", err);
      setMicPermissionGranted(false);
      setMicState('BLOCKED');
      triggerSoundTone(300, 150, 0.3);
    }
  };

  // Cleanup microphones on component unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        try {
          if (audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close();
          }
        } catch (e) {}
      }
    };
  }, []);

  // Speech Recognition browser connector hook bindings
  useEffect(() => {
    const SpeechLib = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechLib) {
      setHasVoiceRecognition(false);
      return;
    }

    let rec: any = null;
    if (micState === 'LISTENING') {
      try {
        rec = new SpeechLib();
        rec.continuous = true;
        rec.interimResults = false;
        rec.lang = 'en-US';

        rec.onstart = () => {};

        rec.onerror = (e: any) => {
          console.error("Speech Recognition capture error:", e);
          if (e.error === 'not-allowed') {
            setMicState('BLOCKED');
            setMicPermissionGranted(false);
          }
        };

        rec.onresult = (event: any) => {
          const resultText = event.results[event.results.length - 1][0].transcript;
          // Verify that signal level crosses current threshold gate before processing!
          const sensitivityGate = getSensitivityGateDb(voiceSensitivity);
          if (liveDb >= sensitivityGate || voiceSensitivity === 'MAX') {
            handleVerbalCommand(resultText);
          } else {
            console.warn(`Continuous voice ignored: decibel level of ${liveDb}dB is below threshold ${sensitivityGate}dB`);
          }
        };

        rec.onend = () => {
          if (micState === 'LISTENING') {
            try { rec.start(); } catch (err) {}
          }
        };

        rec.start();
      } catch (err) {
        console.error(err);
      }
    }

    return () => {
      if (rec) {
        try {
          rec.abort();
        } catch (e) {}
      }
    };
  }, [micState, voiceSensitivity, liveDb]);

  // Parse spoken transcripts and execute dynamic simulation updates 
  const handleVerbalCommand = (rawText: string, triggerOverrideType?: 'VOICE' | 'MANUAL' | 'DEMO' | 'SYSTEM') => {
    const text = rawText.toLowerCase().trim();
    setSpokenTranscript(rawText);

    // Audio output sound synthesis indicator
    playRoutingSuccessBeep();

    let nodeMatched = 'NODE-21.5 GENERAL ROUTER';
    let label = 'VERBAL HANDSHAKE MATCH';
    
    if (text.includes('launch') || text.includes('take off') || text.includes('swarm') || text.includes('ascend')) {
      setTelemetry({ alt: 350, bat: 95 });
      setProposalReady(true);
      nodeMatched = 'Node-09.2 (Antigravity Swarm)';
      label = 'FLIGHT ORBIT SEQUENCE ARMED';
      triggerSoundTone(600, 1000, 0.25);
      speakCommandStatus("Ascending drone swarm. Altitude target set to 350 meters.");
    } 
    else if (text.includes('land') || text.includes('abort') || text.includes('stop')) {
      setTelemetry({ alt: 0, bat: 89 });
      setActiveVoiceDome(false);
      setOrbitMultiplier(1);
      nodeMatched = 'Node-17 (Chronos Ledger)';
      label = 'FLEET GRACEFUL SHUTDOWN';
      triggerSoundTone(400, 150, 0.4);
      speakCommandStatus("Descending drone swarm gracefully. Powering motors to zero.");
    }
    else if (text.includes('pyro') || text.includes('firework') || text.includes('ignite') || text.includes('gavel')) {
      nodeMatched = 'Node-16 (The Gavel)';
      label = 'PYRO TECHNICS BURST INJECTED';
      triggerFireworksBursts();
      triggerSoundTone(120, 300, 0.3);
      speakCommandStatus("Igniting synchronized tactical pyrotechnics.");
    }
    else if (text.includes('spin') || text.includes('matrix') || text.includes('orbit')) {
      nodeMatched = 'Node-06 (The Oracle)';
      label = 'KINETIC VELOCITY SPEED MULTIPLIED';
      setOrbitMultiplier(4.5);
      triggerSoundTone(900, 1200, 0.15);
      speakCommandStatus("Matrix orbital acceleration factor set to three point five.");
    }
    else if (text.includes('shield') || text.includes('protect') || text.includes('defend')) {
      nodeMatched = 'Node-01 (Vanguard-Closer)';
      label = 'DEFENSIVE KINETIC GRID DOME ON';
      setActiveVoiceDome(true);
      triggerSoundTone(500, 420, 0.35);
      speakCommandStatus("Defensive kinetic electromagnetic shield engaged.");
    }
    else if (text.includes('director') || text.includes('route') || text.includes('headquarters') || text.includes('home') || text.includes('co.uk')) {
      nodeMatched = 'NexoraX Core HQ';
      label = 'AUTOPILOT LOCK: DUNFERMLINE SecurePayment NODE';
      setVenue("Nexorax Dunfermline HQ Direct Target (Sort Code: Verified Payment Configuration)");
      triggerSoundTone(700, 750, 0.1);
      speakCommandStatus("Autopilot route target synchronized with Dunfermline Headquarters.");
    }

    setLastActionNode(`${nodeMatched} - [${label}]`);

    // Determine trigger origin
    const origin: 'VOICE' | 'MANUAL' | 'DEMO' | 'SYSTEM' = triggerOverrideType || (micState === 'LISTENING' ? 'VOICE' : 'MANUAL');
    
    // Trigger visual simulation peak wave burst for manual clicks & demo commands
    if (origin === 'MANUAL' || origin === 'DEMO') {
      setSimulatingVoiceWave(true);
      setTimeout(() => {
        setSimulatingVoiceWave(false);
      }, 750);
    }
    const computedLatency = origin === 'VOICE' ? 800 : origin === 'DEMO' ? 50 : 150;

    // Append history telemetry list
    setVoiceRouteLogs(prev => [
      {
        id: String(Date.now()),
        phrase: rawText.toUpperCase(),
        routeNode: nodeMatched,
        handshake: `${computedLatency / 1000}s SUCCESS`,
        time: new Date().toLocaleTimeString(),
        triggerType: origin,
        sensitivityValue: origin === 'VOICE' ? voiceSensitivity : 'BYPASS',
        latencyMs: computedLatency
      },
      ...prev.slice(0, 49) // Keep last 50 logs for deep diagnostics
    ]);
  };

  // Automated Autonomous Drone Demo Loop Scheduler
  useEffect(() => {
    if (!demoLoopActive) {
      setDemoLoopStep(0);
      return;
    }

    const commandsSequence = [
      { text: 'launch swarm' },
      { text: 'matrix spin' },
      { text: 'ignite pyro' },
      { text: 'activate shield' },
      { text: 'director route' },
      { text: 'land drones' }
    ];

    const timer = setInterval(() => {
      const commandToRun = commandsSequence[demoLoopStep];
      handleVerbalCommand(commandToRun.text, 'DEMO');
      setDemoLoopStep(prev => (prev + 1) % commandsSequence.length);
    }, demoLoopSpeedSeconds * 1000);

    return () => clearInterval(timer);
  }, [demoLoopActive, demoLoopStep, demoLoopSpeedSeconds]);

  // Helper browser sound tone generator (sine sweeps)
  function triggerSoundTone(startFreq: number, endFreq: number, duration: number) {
    if (isVoiceMuted) return;
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(startFreq, audioCtx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(endFreq, audioCtx.currentTime + duration);
      
      gain.gain.setValueAtTime(0.04, audioCtx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + duration);
      
      osc.start();
      osc.stop(audioCtx.currentTime + duration);
    } catch (e) {}
  }

  const playRoutingSuccessBeep = () => {
    triggerSoundTone(880, 1200, 0.12);
  };

  // Speaks command statuses to realize voice feed-back synthesis channel
  const speakCommandStatus = (phrase: string) => {
    if (isVoiceMuted) return;
    if ('speechSynthesis' in window) {
      try {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(phrase);
        utterance.pitch = 1.05;
        utterance.rate = 1.02;
        
        // Pick high fidelity voice
        const voices = window.speechSynthesis.getVoices();
        const bestVoice = voices.find(v => {
          const name = v.name.toLowerCase();
          return (name.includes('female') || name.includes('zira') || name.includes('samantha') || name.includes('google')) && v.lang.startsWith('en');
        });
        if (bestVoice) {
          utterance.voice = bestVoice;
        }
        
        window.speechSynthesis.speak(utterance);
      } catch (err) {
        console.warn('Speech synthesis feedback swept out:', err);
      }
    }
  };

  // Triggers glowing animated coordinate SVG bursts
  const triggerFireworksBursts = () => {
    const burstColors = ['#f43f5e', '#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#06b6d4', '#a855f7'];
    const newParticles = Array.from({ length: 12 }).map((_, idx) => ({
      id: Date.now() + idx,
      x: 30 + Math.random() * 130, 
      y: 30 + Math.random() * 130,
      color: burstColors[Math.floor(Math.random() * burstColors.length)]
    }));
    
    setPyroParticles(prev => [...prev, ...newParticles]);
    
    // Auto purge coordinates on cycle expiration
    setTimeout(() => {
      setPyroParticles(prev => prev.filter(p => !newParticles.includes(p)));
    }, 1200);
  };

  const calculateFinancials = () => {
    const gross = parseFloat(budget) || 0;
    const levy = gross * 0.01;
    const gavel = (gross - levy) * 0.20;
    const vat = gross * 0.20; 
    const net = gross - levy - gavel - vat;
    return { gross, levy, gavel, vat, net };
  };

  const handleGenerateProposal = (e: React.FormEvent) => {
    e.preventDefault();
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      setProposalReady(true);
    }, 1500);
  };

  const financials = calculateFinancials();

  return (
    <div className="flex flex-col h-full bg-[#030014] text-slate-300 border border-violet-900/40 rounded-2xl overflow-hidden font-mono relative shadow-[0_0_50px_-15px_rgba(139,92,246,0.35)]">
      
      {/* Dynamic Header Tab Rails */}
      <div className="border-b border-violet-900/50 bg-black/80 p-5 flex flex-col xl:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4 text-left">
          <div className="w-12 h-12 rounded bg-violet-950/50 border border-violet-500/50 flex items-center justify-center shadow-[0_0_15px_rgba(139,92,246,0.5)]">
            <Zap className="w-6 h-6 text-violet-400 animate-pulse" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[9px] bg-violet-900/40 border border-violet-500/30 text-violet-300 font-extrabold px-1.5 py-0.5 rounded tracking-wider uppercase">
                NODE-21.5
              </span>
              <h2 className="text-violet-200 font-black text-lg uppercase tracking-[0.15em] flex items-center gap-2">
                SPECTACLE COMMAND 
              </h2>
            </div>
            <p className="text-[9px] text-violet-400/70 uppercase tracking-widest mt-0.5">Autonomous Drone & Voice Coordinate Orchestration</p>
          </div>
        </div>

        {/* Tactical Control Switching Board */}
        <div className="flex flex-wrap bg-[#05030f] p-1 border border-violet-900/50 rounded-xl">
          <button 
            onClick={() => setActiveTab('PROPOSAL')}
            className={`px-4 py-2 text-[9px] xl:text-[10px] font-extrabold uppercase tracking-widest transition-colors rounded ${activeTab === 'PROPOSAL' ? 'bg-violet-900/50 text-white shadow' : 'text-violet-500/60 hover:text-white'}`}
          >
            PROPOSAL BUILDER
          </button>
          
          <button 
            onClick={() => setActiveTab('TELEMETRY')}
            className={`px-4 py-2 text-[9px] xl:text-[10px] font-extrabold uppercase tracking-widest transition-colors rounded flex items-center gap-2 ${activeTab === 'TELEMETRY' ? 'bg-violet-900/50 text-white shadow' : 'text-violet-500/60 hover:text-white'}`}
          >
            <Activity className="w-3.5 h-3.5" /> 3D TELEMETRY
          </button>

          <button 
            onClick={() => setActiveTab('VOICE_CONSOLE')}
            className={`px-4 py-2 text-[9px] xl:text-[10px] font-extrabold uppercase tracking-widest transition-colors rounded flex items-center gap-2 border ${
              activeTab === 'VOICE_CONSOLE' 
                ? 'bg-emerald-950/60 text-emerald-300 border-emerald-500/40 shadow-[0_0_15px_rgba(16,185,129,0.2)]' 
                : 'text-emerald-500/50 border-transparent hover:text-emerald-400'
            }`}
          >
            <Mic className="w-3.5 h-3.5 text-emerald-400 animate-pulse" /> VOICE DESK CONTROL
          </button>

          <button 
            onClick={() => setActiveTab('HOTKEYS')}
            className={`px-4 py-2 text-[9px] xl:text-[10px] font-extrabold uppercase tracking-widest transition-colors rounded flex items-center gap-2 border ${
              activeTab === 'HOTKEYS' 
                ? 'bg-violet-950/60 text-violet-300 border-violet-500/40 shadow-[0_0_15px_rgba(139,92,246,0.2)]' 
                : 'text-violet-500/50 border-transparent hover:text-violet-400'
            }`}
          >
            <Keyboard className="w-3.5 h-3.5" /> HOTKEY REGISTER
          </button>
        </div>
      </div>

      {/* Primary Display Windows */}
      <div className="flex-1 overflow-hidden relative min-h-[580px]">
        <AnimatePresence mode="wait">
          
          {/* TAB 1: PROPOSAL BUILDER FORM */}
          {activeTab === 'PROPOSAL' && (
            <motion.div 
              key="proposal"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute inset-0 p-6 md:p-8 overflow-y-auto"
            >
              <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
                
                {/* Brief Section */}
                <div className="lg:col-span-7 bg-black/40 p-6 rounded-xl border border-violet-900/30 flex flex-col justify-between">
                  <div>
                    <h3 className="text-violet-300 font-extrabold mb-5 flex items-center gap-2 uppercase tracking-widest text-xs border-b border-violet-900/30 pb-3">
                      <FileCheck className="w-4 h-4 text-violet-400" /> SYSTEM BRIEFING MANIFEST
                    </h3>
                    
                    <form onSubmit={handleGenerateProposal} className="space-y-4">
                      <div>
                        <label className="text-[9px] text-violet-400/90 uppercase tracking-widest mb-1.5 block font-bold">EVENT VENUE OR CODELINK TARGET</label>
                        <div className="relative">
                          <Map className="w-4 h-4 text-violet-500/50 absolute left-3 top-3" />
                          <input 
                            type="text" 
                            required
                            value={venue}
                            onChange={e => setVenue(e.target.value)}
                            className="w-full bg-black/50 border border-violet-900/50 rounded-lg pl-10 pr-4 py-2.5 text-xs text-white focus:outline-none focus:border-violet-500 transition-colors placeholder-violet-900/30 font-mono"
                            placeholder="e.g. Burj Khalifa Dubai Area (Coordinates)"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-[9px] text-violet-400/90 uppercase tracking-widest mb-1.5 block font-bold">TOTAL SPECTACLE AUDIT BUDGET (£)</label>
                        <div className="relative">
                          <DollarSign className="w-4 h-4 text-violet-500/50 absolute left-3 top-3" />
                          <input 
                            type="number" 
                            required
                            value={budget}
                            onChange={e => setBudget(e.target.value)}
                            className="w-full bg-black/50 border border-violet-900/50 rounded-lg pl-10 pr-4 py-2.5 text-xs text-white focus:outline-none focus:border-violet-500 transition-colors placeholder-violet-900/30 font-mono"
                            placeholder="e.g. 750000"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-[9px] text-violet-400/90 uppercase tracking-widest mb-1.5 block font-bold font-mono">DRONE ARMAMENTS</label>
                          <select 
                            value={fleetSize}
                            onChange={e => setFleetSize(e.target.value)}
                            className="w-full bg-black/50 border border-violet-900/50 rounded-lg p-2.5 text-xs text-white focus:outline-none focus:border-violet-500 transition-colors font-mono font-bold"
                          >
                            <option value="100">100 UNITS (ALPHA)</option>
                            <option value="450">450 UNITS (GRID)</option>
                            <option value="1200">1,200 UNITS (MASSIVE)</option>
                            <option value="5000">5,000 UNITS (NEXORAX)</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[9px] text-violet-400/90 uppercase tracking-widest mb-1.5 block font-bold font-mono">PYRO PAYLOADS</label>
                          <select 
                            value={pyroLevel}
                            onChange={e => setPyroLevel(e.target.value)}
                            className="w-full bg-black/50 border border-violet-900/50 rounded-lg p-2.5 text-xs text-white focus:outline-none focus:border-violet-500 transition-colors font-mono font-bold"
                          >
                            <option value="NONE">NO PYRO CORES</option>
                            <option value="STANDARD">STANDARD GAVEL EXPLOSION</option>
                            <option value="HIGH_ALTITUDE">HIGH ALTITUDE SHELLS</option>
                            <option value="MAX_IMPACT">METRIC OVERDRIVE MAXIMUM</option>
                          </select>
                        </div>
                      </div>

                      <div className="pt-2">
                        <button 
                          type="submit" 
                          disabled={isGenerating || !budget}
                          className="w-full bg-violet-600 hover:bg-violet-500 text-white font-extrabold py-3 rounded-lg text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        >
                          {isGenerating ? (
                            <><Activity className="w-4 h-4 animate-spin text-white" /> AGGREGATING SIMULATIONS...</>
                          ) : proposalReady ? (
                            <><CheckCircle className="w-4 h-4 text-emerald-400" /> MATRIX REPLICATED READY</>
                          ) : (
                            <><Cuboid className="w-4 h-4 text-violet-300" /> BUILD SPECIFICATION PLAN</>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>

                  <div className="mt-6 p-4 bg-violet-950/10 border border-violet-900/35 rounded-lg">
                    <span className="text-[8px] text-violet-400 font-extrabold uppercase block tracking-widest mb-1">
                      INTEGRITY VERIFICATION SYSTEM
                    </span>
                    <p className="text-[10px] text-slate-400 leading-relaxed font-mono">
                      All calculations adhere strictly to UK local tax codes under Lead Director financial directives. 1% 3% Community Allocation & 20% Lifetime 10% Maintenance & Hosting Fee are accounted for instantly dynamically below.
                    </p>
                  </div>
                </div>

                {/* Right hand financials breakdown */}
                <div className="lg:col-span-5 flex flex-col justify-between space-y-4">
                  
                  <div className="bg-[#05030f] border border-violet-850/40 rounded-xl p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <h4 className="text-violet-400 font-black mb-4 uppercase tracking-widest text-[10.5px] border-b border-violet-900/50 pb-2">
                        FINANCIAL ROYALTIES PARSING
                      </h4>
                      
                      <div className="space-y-2 text-xs font-mono">
                        <div className="flex justify-between text-slate-400 py-1 border-b border-stone-900">
                          <span>CLIENT GROSS CONTRACT:</span>
                          <span className="text-white font-black">£{financials.gross.toLocaleString(undefined, {minimumFractionDigits:2})}</span>
                        </div>
                        <div className="flex justify-between text-[#10b981] py-1 border-b border-stone-900">
                          <span>1% PERPETUAL LEVY:</span>
                          <span className="font-bold">-£{financials.levy.toLocaleString(undefined, {minimumFractionDigits:2})}</span>
                        </div>
                        <div className="flex justify-between text-indigo-400 py-1 border-b border-stone-900">
                          <span>20% PERFORMANCE TAX:</span>
                          <span>-£{financials.gavel.toLocaleString(undefined, {minimumFractionDigits:2})}</span>
                        </div>
                        <div className="flex justify-between text-slate-500 py-1">
                          <span>20% STANDARD VAT ALLOCATION:</span>
                          <span>-£{financials.vat.toLocaleString(undefined, {minimumFractionDigits:2})}</span>
                        </div>
                        
                        <div className="my-3 border-t border-dashed border-violet-900/50 py-1"></div>
                        
                        <div className="flex justify-between text-[#00f3ff] font-black text-sm bg-violet-950/20 p-3 rounded-lg border border-violet-500/25 shadow-inner">
                          <span>NET SETTLEMENT SECURED:</span>
                          <span>£{financials.net.toLocaleString(undefined, {minimumFractionDigits:2})}</span>
                        </div>
                      </div>
                    </div>

                    <button 
                      disabled={!proposalReady}
                      className="w-full bg-white text-black font-extrabold py-2.5 rounded-lg text-[9.5px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-colors hover:bg-violet-100 disabled:opacity-40 disabled:cursor-not-allowed mt-4 cursor-pointer"
                    >
                      <Send className="w-3.5 h-3.5" /> DISPATCH LEDGER PROPOSAL
                    </button>
                  </div>

                  <div className="bg-black/60 p-4 border border-violet-900/30 rounded-lg flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-indigo-500 animate-ping"></div>
                    <div className="text-[10px] text-slate-400 text-left">
                      <span className="font-bold text-white block uppercase">V8.2 EXPANSION LEDGER ROUTE</span>
                      Synchronized dynamically with the Google Drive NexoraX Resource Library.
                    </div>
                  </div>

                </div>

              </div>
            </motion.div>
          )}

          {/* TAB 2: 3D TELEMETRY ORBITING STREAM */}
          {activeTab === 'TELEMETRY' && (
            <motion.div 
              key="telemetry"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              className="absolute inset-0 p-6 flex flex-col md:flex-row gap-6 text-left"
            >
              {/* Drone Orbit map view */}
              <div className="flex-1 bg-black/60 border border-violet-900/40 rounded-xl relative overflow-hidden p-4 flex flex-col justify-between">
                
                {/* Simulated Radar Orbit Sweep */}
                <div className="relative flex-1 bg-[#010107] border border-slate-900 rounded-lg overflow-hidden flex items-center justify-center">
                  <div className="absolute inset-0 bg-[linear-gradient(rgba(139,92,246,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.06)_1px,transparent_1px)] bg-[size:16px_16px]"></div>
                  
                  {/* Outer Orbit Bounds rings */}
                  <div className="w-64 h-64 border border-violet-900/30 rounded-full flex items-center justify-center relative">
                    <div className="w-48 h-48 border border-violet-500/15 rounded-full flex items-center justify-center">
                      <div className="w-32 h-32 border border-violet-500/10 rounded-full flex items-center justify-center">
                        <div className="w-3 h-3 bg-violet-500 rounded-full animate-pulse shadow-[0_0_15px_rgba(139,92,246,1)] z-10"></div>
                      </div>
                    </div>

                    {/* Orbit Sweep scanning vector lines */}
                    <div className="absolute w-full h-[1px] bg-violet-700/10"></div>
                    <div className="absolute h-full w-[1px] bg-violet-700/10"></div>

                    {/* Simulated Orbiting Drone Node Dots */}
                    {[1, 2, 3, 4].map((id) => (
                      <motion.div
                        key={id}
                        animate={{ rotate: 360 }}
                        transition={{ duration: 6 / id, repeat: Infinity, ease: 'linear' }}
                        className="absolute inset-0 z-10"
                      >
                        <div 
                          className="w-2.5 h-2.5 bg-violet-400 rounded-full border border-white absolute shadow-[0_0_8px_rgba(139,92,246,0.8)]"
                          style={{
                            top: `${15 + id * 15}%`,
                            left: '50%',
                            translate: '-50% -50%'
                          }}
                        >
                          <span className="absolute bottom-3 left-3 bg-black/80 border border-violet-500/20 rounded px-1 text-[7px] text-violet-300 font-bold">
                            UAS-{id}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="absolute bottom-3 left-3 text-[9px] text-violet-400/80 font-mono tracking-wide leading-relaxed">
                    RADAR ORBIT STATUS: SCANNING MATRIX GRID... <br />
                    VECTOR SYNCRONIZATION: DIRECT CO-CHANNEL METRIC
                  </div>
                </div>

                <div className="mt-4 flex justify-between items-center text-[10px] text-slate-500 font-bold border-t border-violet-900/30 pt-3">
                  <span>TELEMETRY STABLE AT 3000m REF</span>
                  <span className="text-[#a855f7]">NORMAL ACTIVE DEPLOY</span>
                </div>
              </div>

              {/* Sidebar flight controllers state stats */}
              <div className="w-full md:w-80 flex flex-col justify-between space-y-4">
                <div className="bg-[#05030f]/90 border border-violet-900/45 rounded-xl p-5 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xs font-black text-violet-300 uppercase tracking-widest border-b border-violet-900/55 pb-3 mb-4">
                      FLEET CRITICAL METRICS
                    </h3>

                    <div className="space-y-4 font-mono text-xs">
                      <div className="bg-black/40 p-3 rounded-lg border border-slate-900">
                        <span className="text-[7.5px] text-slate-500 font-black tracking-wider block mb-1">
                          ACTIVE DRONE IN-FLIGHT FLEETS
                        </span>
                        <span className="text-xl font-black text-white">{fleetSize} UNITS OPERATING</span>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-black/40 p-3 rounded-lg border border-slate-900">
                          <span className="text-[7.5px] text-slate-500 font-black tracking-wider block mb-1">
                            ALTITUDE STREAMS
                          </span>
                          <span className="text-sm font-bold text-[#00f0ff]">{telemetry.alt}m RAMPED</span>
                        </div>
                        <div className="bg-black/40 p-3 rounded-lg border border-slate-900">
                          <span className="text-[7.5px] text-slate-500 font-black tracking-wider block mb-1">
                            BATTERY CAPACITY
                          </span>
                          <span className={`text-sm font-bold ${telemetry.bat < 15 ? 'text-rose-500 animate-pulse' : 'text-emerald-400'}`}>{telemetry.bat}%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-rose-950/20 border border-rose-500/25 rounded-lg text-[9px] text-rose-400 leading-relaxed font-bold mt-6">
                    MANDATORY: INCURSION ALARMS TRACK AUTO ON SATELLITE CHANNELS. RED SHIELDS ENGAGE AUTOMATICALLY ON VOLTAGE GAPS.
                  </div>
                </div>
              </div>

            </motion.div>
          )}

          {/* TAB 3: VOICE ACTIVATION FLIGHT CONSOLE & CONTROLLER */}
          {activeTab === 'VOICE_CONSOLE' && (
            <motion.div 
              key="voice"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className="absolute inset-0 p-6 overflow-y-auto"
            >
              <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
                
                {/* Left hand: Mic controls and live interactive orbiting map */}
                <div className="lg:col-span-7 flex flex-col space-y-6">
                  
                  {/* Microphone controller access card */}
                  <div className="bg-[#04020a] border border-violet-900/35 rounded-xl p-5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 blur-[50px] rounded-full pointer-events-none"></div>

                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div>
                        <h4 className="text-xs font-black text-emerald-400 uppercase tracking-widest flex items-center gap-1.5 mb-1.5">
                          <Mic className="w-4 h-4 text-emerald-400 animate-pulse" />
                          MICROPHONE ACCESS OVERWATCH CONSOLE
                        </h4>
                        <p className="text-[9.5px] text-slate-400 uppercase tracking-wider leading-relaxed">
                          Speak authorized directives directly into your system microphone. Handshakes resolve at 0.8s.
                        </p>
                      </div>

                      {/* Microphone activation switch button */}
                      <button
                        onClick={initiateMicrophoneStream}
                        className={`px-5 py-2.5 rounded-lg font-black font-mono text-[9px] uppercase tracking-widest transition-all cursor-pointer border ${
                          micState === 'LISTENING' 
                            ? 'bg-rose-950/40 text-rose-400 border-rose-500/40 animate-pulse' 
                            : micState === 'BLOCKED'
                            ? 'bg-stone-900 text-stone-500 border-stone-800 cursor-not-allowed'
                            : 'bg-[#10b981]/10 text-emerald-400 border-emerald-500/30 hover:bg-[#10b981]/20'
                        }`}
                        disabled={micState === 'BLOCKED'}
                      >
                        {micState === 'LISTENING' ? '[ DEACTIVATE MIC ]' : '[ ENGAGE MICROPHONE ]'}
                      </button>
                    </div>

                     {/* Microphone Access Indicator Panel */}
                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-black/40 border border-slate-900 p-3 rounded-lg">
                        <span className="text-[7.5px] text-slate-500 font-extrabold uppercase block tracking-widest mb-0.5">MIC CAPTURE</span>
                        <span className={`text-[10px] font-black tracking-widest block uppercase font-mono ${micState === 'LISTENING' ? 'text-emerald-400' : 'text-slate-400'}`}>
                          {micState === 'LISTENING' ? '● ONLINE' : '○ OFFLINE'}
                        </span>
                      </div>
                      <div className="bg-black/40 border border-slate-900 p-3 rounded-lg">
                        <span className="text-[7.5px] text-slate-500 font-extrabold uppercase block tracking-widest mb-0.5">PERMISSIONS STATE</span>
                        <span className={`text-[10px] font-black block uppercase font-mono ${micPermissionGranted === true ? 'text-[#00ffff]' : micPermissionGranted === false ? 'text-rose-500' : 'text-yellow-500'}`}>
                          {micPermissionGranted === true ? 'GRANTED ✓' : micPermissionGranted === false ? 'BLOCKED ✗' : 'AWAITING AUTH'}
                        </span>
                      </div>
                      <div className="bg-black/40 border border-slate-900 p-3 rounded-lg">
                        <span className="text-[7.5px] text-slate-500 font-extrabold uppercase block tracking-widest mb-0.5">BROWSER COG</span>
                        <span className="text-[10px] font-black text-slate-300 block font-mono">
                          {hasVoiceRecognition ? 'APPROVED ✓' : 'LIMITED ✗'}
                        </span>
                      </div>
                      
                      {/* VOICE MUTE MODULE */}
                      <button
                        onClick={() => {
                          setIsVoiceMuted(!isVoiceMuted);
                          triggerSoundTone(isVoiceMuted ? 800 : 300, isVoiceMuted ? 1200 : 150, 0.15);
                        }}
                        className={`border p-3 rounded-lg text-left transition-all hover:scale-[1.02] cursor-pointer flex flex-col justify-between ${
                          isVoiceMuted 
                            ? 'bg-rose-950/20 border-rose-500/40 text-rose-400 shadow-[0_0_12px_rgba(239,68,68,0.15)]' 
                            : 'bg-black/45 border-slate-900 text-emerald-400 hover:border-[#10b981]/30'
                        }`}
                      >
                        <span className="text-[7.5px] text-slate-500 font-extrabold uppercase block tracking-widest mb-0.5">ACOUSTIC MUTE</span>
                        <span className="text-[10px] font-black uppercase font-mono flex items-center gap-1">
                          {isVoiceMuted ? (
                            <>
                              <VolumeX className="w-3.5 h-3.5" /> MUTED
                            </>
                          ) : (
                            <>
                              <Volume2 className="w-3.5 h-3.5 animate-pulse" /> LIVE SOUND
                            </>
                          )}
                        </span>
                      </button>
                    </div>

                    {/* NEW MODULE: VOICE SENSITIVITY LIMITS */}
                    <div className="mt-4 border-t border-slate-900/60 pt-3 text-left">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-[7.5px] text-slate-500 font-extrabold uppercase tracking-widest flex items-center gap-1 font-mono">
                          <SlidersHorizontal className="w-3 h-3 text-violet-400" />
                          COGNITION INTERCEPT SENSITIVITY LIMITS
                        </span>
                        <span className="text-[8px] text-fuchsia-400 font-mono font-bold uppercase tracking-wider">
                          ACTIVE STACK: {voiceSensitivity} GATE
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 font-mono">
                        {(['LOW', 'MEDIUM', 'HIGH', 'MAX'] as const).map((level) => {
                          const isSel = voiceSensitivity === level;
                          const desc = level === 'LOW' ? '0.4x Gain Gate' : level === 'MEDIUM' ? '1.0x Def Gate' : level === 'HIGH' ? '1.6x High Pres' : '2.5x Hyper Inter';
                          return (
                            <button
                              key={level}
                              onClick={() => {
                                setVoiceSensitivity(level);
                                triggerSoundTone(isSel ? 300 : 500, isSel ? 350 : 700, 0.08);
                              }}
                              className={`p-1.5 rounded-lg text-center cursor-pointer transition-all flex flex-col justify-between items-center border ${
                                isSel
                                  ? 'bg-violet-950/40 text-violet-300 border-[#a855f7]/50 shadow-[0_0_10px_rgba(168,85,247,0.15)]'
                                  : 'bg-black/30 text-slate-500 border-slate-900/50 hover:bg-black/60 hover:text-slate-300'
                              }`}
                            >
                              <span className="text-[8.5px] font-black">{level}</span>
                              <span className="text-[6.5px] text-slate-500 mt-1">{desc}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* NEW MODULE: AUTONOMOUS FLIGHT PERFORMANCE DEMO LOOP */}
                    <div className="mt-4 border-t border-slate-900/60 pt-3 text-left">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-[7.5px] text-slate-500 font-extrabold uppercase tracking-widest flex items-center gap-1 font-mono">
                          <RefreshCw className={`w-3 h-3 text-[#10b981] ${demoLoopActive ? 'animate-spin' : ''}`} />
                          AUTONOMOUS FLIGHT PERFORMANCE DEMO LOOP
                        </span>
                        {demoLoopActive && (
                          <span className="text-[7.5px] bg-[#00ffff]/10 border border-[#00f0ff]/20 text-[#00f0ff] font-bold px-1.5 py-0.5 rounded font-mono uppercase tracking-wider animate-pulse">
                            ACTIVE DEMO LOOP RUNNING
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                        {/* Engage Auto Loop Button */}
                        <div className="sm:col-span-5">
                          <button
                            onClick={() => {
                              setDemoLoopActive(!demoLoopActive);
                              triggerSoundTone(demoLoopActive ? 300 : 800, demoLoopActive ? 150 : 1200, 0.2);
                            }}
                            className={`w-full py-2 px-3 rounded-lg font-mono text-[9px] font-black uppercase tracking-widest transition-all cursor-pointer border ${
                              demoLoopActive
                                ? 'bg-emerald-950/40 text-emerald-300 border-emerald-500/50 hover:bg-emerald-900/20'
                                : 'bg-black/40 text-slate-400 border-slate-900 hover:bg-[#10b981]/10 hover:border-[#10b981]/30 hover:text-emerald-400'
                            }`}
                          >
                            {demoLoopActive ? '[ STOP AUTO PILOT ]' : '[ ENGAGE AUTO PILOT ]'}
                          </button>
                        </div>

                        {/* Speed controller slider */}
                        <div className="sm:col-span-7 flex items-center justify-between gap-3 bg-black/40 border border-slate-900/80 p-2 rounded-lg">
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-[7px] text-slate-500 font-bold uppercase font-mono">STEP INTERVAL CONSTANT</span>
                              <span className="text-[8.5px] text-[#a855f7] font-mono font-bold">{demoLoopSpeedSeconds}s RATE</span>
                            </div>
                            <input
                              type="range"
                              min="2"
                              max="10"
                              step="1"
                              value={demoLoopSpeedSeconds}
                              onChange={(e) => {
                                setDemoLoopSpeedSeconds(Number(e.target.value));
                                triggerSoundTone(650, 680, 0.05);
                              }}
                              className="w-full accent-[#a855f7] bg-slate-900 h-1 rounded cursor-pointer"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Horizontal steps cycle progression visualizer if active */}
                      {demoLoopActive && (
                        <div className="mt-3 bg-black/75 border border-violet-950/45 rounded-lg p-2.5">
                          <span className="text-[7px] text-slate-500 font-extrabold uppercase block tracking-widest mb-1.5 font-mono">
                            AUTOPILOT STEP SEQUENCE COGNITION FLOW
                          </span>
                          <div className="grid grid-cols-6 gap-1 font-mono text-center">
                            {[
                              { label: 'TAKE OFF', step: 0, node: 'N-09.2' },
                              { label: 'SPIN VELO', step: 1, node: 'N-06' },
                              { label: 'GAVEL PYR', step: 2, node: 'N-16' },
                              { label: 'SYNC SHLD', step: 3, node: 'N-01' },
                              { label: 'SYNC HQ', step: 4, node: 'HQCore' },
                              { label: 'DESCENT', step: 5, node: 'N-17' }
                            ].map((item, idx) => {
                              const isActive = demoLoopStep === item.step;
                              const isPassed = demoLoopStep > item.step;
                              return (
                                <div
                                  key={idx}
                                  className={`p-1.5 rounded transition-all flex flex-col justify-center items-center h-10 border ${
                                    isActive
                                      ? 'bg-[#00f3ff]/10 text-cyan-400 border-cyan-500/50 shadow-[0_0_8px_rgba(0,243,255,0.1)] active animate-pulse'
                                      : isPassed
                                      ? 'bg-emerald-950/20 text-emerald-500/60 border-emerald-950/20'
                                      : 'bg-black/40 text-stone-600 border-stone-900/60'
                                  }`}
                                >
                                  <span className="text-[7.5px] font-black leading-none truncate w-full">{item.label}</span>
                                  <span className="text-[6px] opacity-70 mt-1">{item.node}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                     {/* Dynamic Real-time wave visualization */}
                     <div className="mt-4 bg-black/50 border border-violet-950/40 rounded-lg p-3.5 flex flex-col justify-center relative">
                       <div className="flex items-center justify-between mb-2">
                         <span className="text-[7.5px] text-slate-400 font-extrabold uppercase tracking-widest flex items-center gap-1.5">
                          LIVE AUDIO COGNITION OSCILLOSCOPE SPECTRA
                          {isVoiceMuted && (
                            <span className="text-[6.5px] bg-rose-955/45 text-rose-450 border border-rose-500/10 px-1.5 py-0.5 rounded font-black tracking-widest">
                              MUTED
                            </span>
                          )}
                        </span>
                         <span className={`text-[8px] font-black tracking-widest font-mono uppercase ${
                           micState === 'LISTENING' && activeSignalMatch 
                             ? 'text-emerald-400 animate-pulse' 
                             : micState === 'LISTENING' 
                             ? 'text-yellow-500' 
                             : 'text-violet-400'
                         }`}>
                           {micState === 'LISTENING' && activeSignalMatch 
                             ? '[ GATE BREACHED - MATCHING COGNITION ]' 
                             : micState === 'LISTENING' 
                             ? '[ CAPTURING DIRECT AUDIO ]' 
                             : '[ STANDBY SCANNING ACTIVE ]'}
                         </span>
                       </div>

                       {/* Live Physical Parameter Readouts Row */}
                       <div className="grid grid-cols-3 gap-2 py-1.5 px-2 mb-3 bg-black/70 border border-slate-900 rounded font-mono text-[7.5px] uppercase tracking-wider text-slate-400">
                         <div>
                           SIGNAL POWER: <span className={`font-black ${liveDb > getSensitivityGateDb(voiceSensitivity) ? 'text-emerald-400' : 'text-slate-200'}`}>{liveDb} dBFS</span>
                         </div>
                         <div>
                           PEAK SPECTRUM: <span className="font-black text-cyan-400">{liveHz} Hz</span>
                         </div>
                         <div>
                           INTERCEPT LIMIT: <span className="font-black text-fuchsia-400">{getSensitivityGateDb(voiceSensitivity)} dBFS</span>
                         </div>
                       </div>
                       
                       {/* Bar frequency curves container with nested threshold line */}
                       <div className="flex h-16 justify-center items-end gap-1 px-4 relative bg-black/30 rounded border border-slate-900/60 pt-3 pb-1">
                         {/* Dynamic Intercept Sensitivity Level Line representing decibel break gate limits */}
                         <div 
                           style={{ 
                             bottom: `${Math.min(95, Math.max(5, 6 + ((getSensitivityGateDb(voiceSensitivity) + 85) / 85) * 94))}%`
                           }}
                           className="absolute left-0 right-0 border-t border-dashed border-fuchsia-500/70 z-10 transition-all duration-300"
                         >
                           <span className="absolute right-2 -top-2.5 text-[5.5px] font-bold text-fuchsia-400 font-mono tracking-wider bg-black/80 px-1 py-0.5 rounded uppercase border border-fuchsia-950/40">
                             GATE LIMIT
                           </span>
                         </div>

                         {simulatedAmplitude.map((h, i) => {
                           const crossesGate = micState === 'LISTENING' && liveDb > getSensitivityGateDb(voiceSensitivity);
                           return (
                             <motion.div
                                key={i}
                                animate={{ height: `${h}%` }}
                                transition={{ type: 'spring', stiffness: 220, damping: 14 }}
                                className={`w-2 h-full rounded-t-sm transition-all duration-100 ${
                                  micState === 'LISTENING' && crossesGate
                                    ? 'bg-gradient-to-t from-emerald-600 via-cyan-400 to-[#ffffff] shadow-[0_0_8px_rgba(34,197,94,0.3)]'
                                    : micState === 'LISTENING'
                                    ? 'bg-gradient-to-t from-yellow-700 to-amber-400'
                                    : 'bg-gradient-to-t from-violet-950 via-violet-800 to-[#c084fc] opacity-60'
                                }`}
                             />
                           );
                         })}
                       </div>
                     </div>
                   </div>

                  {/* 3D Voice Activated Vector Flight Map Indicator */}
                  <div className="bg-[#030615] border border-violet-900/40 rounded-xl p-5 relative overflow-hidden flex-1 min-h-[380px] flex flex-col justify-between">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-violet-600/5 blur-[80px] rounded-full pointer-events-none"></div>

                    <div className="flex justify-between items-start border-b border-violet-950/70 pb-3 mb-4 z-10">
                      <div>
                        <h4 className="text-xs font-black text-violet-300 uppercase tracking-widest flex items-center gap-1.5">
                          <Compass className="text-violet-400 w-4 h-4 animate-spin" />
                          NODE-21.5 LIVE TRACE VECTOR (SWARM OVERVIEW)
                        </h4>
                        <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider mt-0.5 block">
                          Coordinates link current flight models state
                        </span>
                      </div>

                      <div className="text-right">
                        <span className="text-[9px] bg-emerald-950/40 border border-emerald-500/20 text-emerald-400 font-black px-2 py-0.5 rounded uppercase">
                          {telemetry.alt > 0 ? 'IN FLIGHT' : 'GROUND CONTROL STATION'}
                        </span>
                      </div>
                    </div>

                    {/* Interactive Animated SVG Vector Core Mapping Layout */}
                    <div className="relative flex-1 bg-black/45 border border-slate-950 rounded-lg flex items-center justify-center overflow-hidden min-h-[220px]">
                      <div className="absolute inset-0 bg-[linear-gradient(rgba(139,92,246,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.04)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
                      
                      {/* Compass Rings */}
                      <div className="w-56 h-56 border border-violet-900/40 rounded-full flex items-center justify-center relative">
                        {/* Defensive Shield kinetic sphere indicator */}
                        <AnimatePresence>
                          {activeVoiceDome && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1.1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              className="absolute inset-0 rounded-full border-2 border-dashed border-[#00f0ff] bg-[#00f0ff]/5 animate-pulse shadow-[0_0_30px_rgba(0,240,255,0.2)]"
                            >
                              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[7px] text-[#00f0ff] font-extrabold uppercase tracking-widest bg-black/90 p-1 border border-[#00f0ff]/30 rounded whitespace-nowrap">
                                KINETIC DOME ACTIVE <Sparkle className="w-2 h-2 text-cyan-400 inline ml-0.5 animate-spin" />
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Orbit Circles */}
                        <div className="w-40 h-40 border border-violet-500/15 rounded-full flex items-center justify-center">
                          <div className="w-24 h-24 border border-violet-500/10 rounded-full flex items-center justify-center">
                            <div className="w-4 h-4 bg-violet-600 rounded-full animate-ping pointer-events-none"></div>
                          </div>
                        </div>

                        {/* Telemetry coordinate centers */}
                        <div className="absolute w-full h-[1px] bg-violet-500/10"></div>
                        <div className="absolute h-full w-[1px] bg-violet-500/10"></div>

                        {/* Swarm Drone vector locations */}
                        {[1, 2, 3].map((u) => (
                          <motion.div
                            key={u}
                            animate={{ rotate: 360 * orbitMultiplier }}
                            transition={{ duration: 7 / u, repeat: Infinity, ease: 'linear' }}
                            className="absolute inset-0"
                          >
                            <div 
                              className={`w-3 h-3 rounded-full border border-white absolute flex items-center justify-center ${
                                telemetry.alt > 0 
                                  ? 'bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.9)]' 
                                  : 'bg-stone-700 shadow-none'
                              }`}
                              style={{
                                top: `${20 + u * 15}%`,
                                left: '50%',
                                translate: '-50% -50%'
                              }}
                            >
                              <span className="text-[6px] font-bold text-black select-none">U{u}</span>
                            </div>
                          </motion.div>
                        ))}

                        {/* Localized Firework Explosion particles emission list */}
                        {pyroParticles.map((pt) => (
                          <motion.div
                            key={pt.id}
                            initial={{ scale: 0.1, opacity: 1 }}
                            animate={{ scale: [1, 2.5], opacity: [1, 0] }}
                            transition={{ duration: 0.9, ease: 'easeOut' }}
                            className="absolute p-4 rounded-full flex items-center justify-center pointer-events-none"
                            style={{
                              left: `${pt.x}%`,
                              top: `${pt.y}%`,
                              transform: 'translate(-50%, -55%)',
                              backgroundColor: `${pt.color}15`,
                              border: `1.5px solid ${pt.color}`
                            }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: pt.color }}></span>
                          </motion.div>
                        ))}
                      </div>

                      <div className="absolute top-2 left-2 text-[8px] text-fuchsia-400 font-mono bg-black/85 px-2 py-1 border border-violet-900/30 rounded uppercase font-bold tracking-widest shadow-lg">
                        AUTOPILOT: {venue ? venue.toUpperCase() : 'COORDINATE UNLOCKED'}
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-violet-900/60 grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-[9px] text-slate-400 font-bold z-10 font-mono">
                      <div className="bg-black/30 p-1.5 rounded border border-slate-950/80">
                        <span className="text-[6.5px] text-slate-500 block uppercase font-sans mb-0.5">ORBIT SPEED</span>
                        <span className="text-[#00ffff] block font-black tracking-wider text-[10px]">{orbitMultiplier > 1 ? '320 KM/H' : telemetry.alt > 0 ? '110 KM/H' : '0 KM/H'}</span>
                      </div>
                      <div className="bg-black/30 p-1.5 rounded border border-slate-950/80">
                        <span className="text-[6.5px] text-slate-500 block uppercase font-sans mb-0.5">CORE THERMALS</span>
                        <span className={`${pyroParticles.length > 0 ? 'text-rose-400 animate-pulse' : 'text-orange-400'} block font-black tracking-wider text-[10px]`}>{pyroParticles.length > 0 ? '142 °C' : telemetry.alt > 0 ? '42 °C' : '23 °C'}</span>
                      </div>
                      <div className="bg-black/30 p-1.5 rounded border border-slate-950/80">
                        <span className="text-[6.5px] text-slate-500 block uppercase font-sans mb-0.5">ALTITUDE STACK</span>
                        <span className="text-[#a855f7] block font-black tracking-wider text-[10px]">{telemetry.alt} MTR</span>
                      </div>
                      <div className="bg-black/30 p-1.5 rounded border border-slate-950/80">
                        <span className="text-[6.5px] text-slate-500 block uppercase font-sans mb-0.5">POWER CAPACITY</span>
                        <span className={`${telemetry.bat < 15 ? 'text-rose-500 animate-pulse' : 'text-emerald-400'} block font-black tracking-wider text-[10px]`}>{telemetry.bat}%</span>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Right hand: Verbal test simulator override, Voice Navigation registry & NexoraX Logs */}
                <div className="lg:col-span-5 flex flex-col space-y-6">

                  {/* SECTION 1: SIMULATE & REGISTRY */}
                  <div className="bg-[#04020a] border border-violet-900/40 rounded-xl p-5 text-left">
                    <h4 className="text-xs font-black text-violet-300 uppercase tracking-widest flex items-center gap-1.5 mb-3 border-b border-violet-950 pb-2">
                      <Play className="w-4 h-4 text-violet-300" />
                      SIMULATE DIRECT VERBAL CONTROLS override
                    </h4>
                    
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider leading-relaxed mb-4">
                      No microphone? Click any tactical blueprint indicator below to simulate saying the command into the Nexorax Nexus Node stack instantly.
                    </p>

                    <div className="flex flex-col gap-2 font-mono mb-4">
                      <button
                        onClick={() => handleVerbalCommand('launch swarm', 'MANUAL')}
                        className="bg-black/60 hover:bg-violet-900/25 border border-violet-500/50 text-white p-2 text-[9px] font-bold uppercase tracking-widest text-left flex justify-between items-center transition-all cursor-pointer rounded-lg"
                      >
                        <span>"LAUNCH SWARM"</span>
                        <span className="text-[7.5px] bg-violet-950 px-1.5 py-0.5 rounded text-violet-300">NODE 09.2</span>
                      </button>

                      <button
                        onClick={() => handleVerbalCommand('land drones', 'MANUAL')}
                        className="bg-black/60 hover:bg-violet-900/25 border border-violet-500/50 text-white p-2 text-[9px] font-bold uppercase tracking-widest text-left flex justify-between items-center transition-all cursor-pointer rounded-lg"
                      >
                        <span>"LAND DRONES"</span>
                        <span className="text-[7.5px] bg-slate-900 px-1.5 py-0.5 rounded text-slate-300">NODE 17</span>
                      </button>

                      <button
                        onClick={() => handleVerbalCommand('ignite pyro', 'MANUAL')}
                        className="bg-black/60 hover:bg-violet-900/25 border border-violet-500/50 text-white p-2 text-[9px] font-bold uppercase tracking-widest text-left flex justify-between items-center transition-all cursor-pointer rounded-lg"
                      >
                        <span>"IGNITE PYRO"</span>
                        <span className="text-[7.5px] bg-rose-950 text-rose-300 px-1.5 py-0.5 rounded border border-rose-500/20">NODE 16</span>
                      </button>

                      <button
                        onClick={() => handleVerbalCommand('matrix spin', 'MANUAL')}
                        className="bg-black/60 hover:bg-violet-900/25 border border-violet-500/50 text-white p-2 text-[9px] font-bold uppercase tracking-widest text-left flex justify-between items-center transition-all cursor-pointer rounded-lg"
                      >
                        <span>"MATRIX SPIN"</span>
                        <span className="text-[7.5px] bg-indigo-950 text-indigo-300 px-1.5 py-0.5 rounded">NODE 06</span>
                      </button>

                      <button
                        onClick={() => handleVerbalCommand('activate shield', 'MANUAL')}
                        className="bg-black/60 hover:bg-violet-900/25 border border-violet-500/50 text-white p-2 text-[9px] font-bold uppercase tracking-widest text-left flex justify-between items-center transition-all cursor-pointer rounded-lg"
                      >
                        <span>"ACTIVATE SHIELD"</span>
                        <span className="text-[7.5px] bg-[#00ffff]/10 text-cyan-400 px-1.5 py-0.5 rounded border border-[#00f0ff]/20">NODE 01</span>
                      </button>

                      <button
                        onClick={() => handleVerbalCommand('director route', 'MANUAL')}
                        className="bg-black/60 hover:bg-violet-900/25 border border-[#00f3ff]/40 text-[#00f3ff] p-2.5 rounded-lg text-[9px] font-extrabold uppercase tracking-widest text-left flex justify-between items-center transition-all cursor-pointer"
                      >
                        <span>"DIRECTOR ROUTE"</span>
                        <span className="text-[7.5px] bg-cyan-950 px-1.5 py-0.5 rounded">CORE HQ</span>
                      </button>
                    </div>

                    <h4 className="text-xs font-black text-violet-300 uppercase tracking-widest flex items-center gap-1.5 mb-2.5 border-t border-violet-950 pt-3">
                      <Terminal className="w-4 h-4 text-violet-400" />
                      NATIVE VOICE NAVIGATION REGISTRY
                    </h4>

                    <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                      {VOICE_COMMANDS_REGISTRY.map((cmd, idx) => (
                        <div key={idx} className="p-2 bg-black/40 border border-violet-950/60 rounded-lg flex flex-col text-[10px] text-left">
                          <div className="flex justify-between items-center font-black">
                            <span className="text-[#a855f7] tracking-wider uppercase text-[8.5px]">"{cmd.trigger}"</span>
                            <span className="text-[6.5px] bg-violet-950/40 text-slate-300 px-1 py-0.5 rounded">
                              {cmd.nodeLink}
                            </span>
                          </div>
                          <span className="text-slate-500 mt-0.5 font-mono text-[7.5px] uppercase">{cmd.action}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* SECTION 2: COMPREHENSIVE NEXORAX VOICE COMMAND LOG */}
                  <div className="bg-[#04020a] border border-violet-900/45 rounded-xl p-5 flex flex-col flex-grow text-left">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-xs font-black text-violet-300 uppercase tracking-widest flex items-center gap-1.5">
                        <Terminal className="w-4 h-4 text-[#10b981] animate-pulse" />
                        NEXORAX VOICE COMMAND LOG
                      </h4>
                      <div className="flex items-center gap-2">
                        <span className="text-[7.5px] bg-violet-950 text-violet-300 border border-violet-800/45 px-2 py-0.5 rounded font-mono font-black">
                          {voiceRouteLogs.filter((log) => {
                            const matchStr = log.phrase.toLowerCase().includes(logSearchQuery.toLowerCase()) || log.routeNode.toLowerCase().includes(logSearchQuery.toLowerCase());
                            return logTypeFilter === 'ALL' ? matchStr : matchStr && log.triggerType === logTypeFilter;
                          }).length} OF {voiceRouteLogs.length} LOGS
                        </span>
                        
                        {/* EXPORT LOG LEDGER BUTTON */}
                        <button
                          onClick={() => {
                            if (voiceRouteLogs.length === 0) return;
                            const logText = voiceRouteLogs.map(log => 
                              `[${log.time}] [${log.triggerType}] [GATE: ${log.sensitivityValue}] LATENCY: ${log.latencyMs}ms\n` +
                              `COMMAND: "${log.phrase}"\n` +
                              `ROUTED NODE: ${log.routeNode}\n` +
                              `--------------------------------------------------`
                            ).join('\n');
                            
                            const blob = new Blob([logText], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `nexorax_voice_commands_${Date.now()}.txt`;
                            link.click();
                            URL.revokeObjectURL(url);
                            triggerSoundTone(900, 1500, 0.15);
                          }}
                          disabled={voiceRouteLogs.length === 0}
                          title="Export Log Ledger"
                          className="p-1 hover:bg-violet-950/40 hover:text-[#00ffff] text-slate-500 rounded cursor-pointer transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>

                        <button
                          onClick={() => {
                            setVoiceRouteLogs([]);
                            triggerSoundTone(150, 80, 0.3);
                          }}
                          disabled={voiceRouteLogs.length === 0}
                          title="Purge Active Command Records"
                          className="p-1 hover:bg-rose-950/40 hover:text-rose-400 text-slate-500 rounded cursor-pointer transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-2.5 leading-relaxed">
                      NexoraX ledger tracing real-time voice, manual, and demo-loop commands resolving on the NexoraX telemetry stack.
                    </p>

                    {/* Filter & Search Bar */}
                    <div className="grid grid-cols-1 gap-2 mb-3 font-mono">
                      {/* Search Bar */}
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="SEARCH LOGS (LAUNCH, SHIELD, HQ)..."
                          value={logSearchQuery}
                          onChange={(e) => setLogSearchQuery(e.target.value)}
                          className="w-full bg-black/60 border border-violet-950 rounded-lg py-1.5 pl-7 pr-3 text-[9px] text-white placeholder-slate-600 focus:outline-none focus:border-violet-500/50"
                        />
                        <Search className="w-3 h-3 text-slate-600 absolute left-2.5 top-1/2 -translate-y-1/2" />
                      </div>

                      {/* Filter Controls Row */}
                      <div className="flex flex-wrap gap-1">
                        {(['ALL', 'VOICE', 'MANUAL', 'DEMO', 'SYSTEM'] as const).map((type) => {
                          const isSel = logTypeFilter === type;
                          return (
                            <button
                              key={type}
                              onClick={() => {
                                setLogTypeFilter(type);
                                triggerSoundTone(450, 480, 0.05);
                              }}
                              className={`px-2 py-1 rounded text-[7.5px] font-black tracking-widest cursor-pointer transition-all border ${
                                isSel
                                  ? 'bg-[#a855f7]/20 text-[#a855f7] border-[#a855f7]/40 shadow-[0_0_8px_rgba(168,85,247,0.1)]'
                                  : 'bg-black/30 text-slate-500 border-slate-950/40 hover:text-slate-300'
                              }`}
                            >
                              {type}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Log Terminal Screen */}
                    <div className="bg-black/95 border border-violet-950/80 rounded-lg p-3 h-56 overflow-y-auto w-full text-xs scrollbar-thin font-mono space-y-3">
                      
                      {/* Current active monitor indicators */}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[8px] border-b border-violet-950/70 pb-2 mb-2 text-slate-500 uppercase">
                        <div>
                          TRANSCRIPT HEAD: <span className="text-white">"{spokenTranscript || 'NONE'}"</span>
                        </div>
                        <div>
                          COGNITION LINK: <span className="text-[#00ffff]">{lastActionNode}</span>
                        </div>
                        <div className="text-violet-400 font-bold ml-auto animate-pulse flex items-center gap-1 text-[7.5px]">
                          ⚡ CLICK ENTRIES TO RE-EXECUTE SIGNATURES
                        </div>
                      </div>

                      {/* Log lists */}
                      {voiceRouteLogs.filter((log) => {
                        const matchStr = log.phrase.toLowerCase().includes(logSearchQuery.toLowerCase()) || log.routeNode.toLowerCase().includes(logSearchQuery.toLowerCase());
                        return logTypeFilter === 'ALL' ? matchStr : matchStr && log.triggerType === logTypeFilter;
                      }).length === 0 ? (
                        <div className="h-28 flex items-center justify-center flex-col text-slate-600 space-y-1 text-[9px] uppercase">
                          <Terminal className="w-5 h-5 text-slate-800 animate-pulse" />
                          <span>Ledger Empty — No logs match the selected filter query</span>
                        </div>
                      ) : (
                        voiceRouteLogs.filter((log) => {
                          const matchStr = log.phrase.toLowerCase().includes(logSearchQuery.toLowerCase()) || log.routeNode.toLowerCase().includes(logSearchQuery.toLowerCase());
                          return logTypeFilter === 'ALL' ? matchStr : matchStr && log.triggerType === logTypeFilter;
                        }).map((log) => {
                          // Tag render configs
                          const isVoice = log.triggerType === 'VOICE';
                          const isManual = log.triggerType === 'MANUAL';
                          const isDemo = log.triggerType === 'DEMO';

                          const tagColor = isVoice 
                            ? 'bg-amber-950/40 text-amber-400 border-amber-500/25'
                            : isManual
                            ? 'bg-sky-950/40 text-sky-400 border-sky-500/25'
                            : isDemo
                            ? 'bg-emerald-950/40 text-emerald-400 border-emerald-500/25'
                            : 'bg-slate-900 border-slate-800 text-slate-400';

                          return (
                            <div 
                              key={log.id} 
                              onClick={() => {
                                handleVerbalCommand(log.phrase.toLowerCase(), 'MANUAL');
                                triggerSoundTone(700, 1100, 0.1);
                              }}
                              title="Click to replay and route this voice signature"
                              className="text-[9px] border-b border-violet-950/30 pb-2 flex flex-col justify-between hover:bg-violet-900/30 cursor-pointer p-1.5 rounded transition-all transform hover:translate-x-0.5 active:scale-[0.99] group"
                            >
                              <div className="flex justify-between items-center mb-1">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-slate-500 font-bold">[{log.time}]</span>
                                  <span className={`text-[6.5px] px-1 py-0.5 rounded border uppercase font-extrabold tracking-widest ${tagColor}`}>
                                    {log.triggerType}
                                  </span>
                                  {log.sensitivityValue !== 'BYPASS' && (
                                    <span className="text-[6.5px] text-fuchsia-400 bg-fuchsia-950/20 px-1 py-0.5 rounded font-mono font-bold">
                                      GATE: {log.sensitivityValue}
                                    </span>
                                  )}
                                </div>
                                <span className="text-emerald-400 font-black tracking-wider text-[8.5px]">
                                  {log.latencyMs}ms HANDSHAKE
                                </span>
                              </div>

                              <div className="flex justify-between items-start gap-2">
                                <span className="text-slate-200 group-hover:text-cyan-300 transition-colors uppercase font-bold break-all max-w-[55%]">
                                  "{log.phrase}"
                                </span>
                                <div className="text-violet-400 text-right text-[8.5px] max-w-[45%] flex flex-col items-end">
                                  <span className="truncate block font-semibold text-violet-400">↳ {log.routeNode}</span>
                                  <span className="text-[6.5px] text-[#00ffff] font-bold font-mono opacity-0 group-hover:opacity-100 transition-opacity block mt-0.5 animate-pulse">
                                    RE-EXECUTE &larr;
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                </div>

              </div>
            </motion.div>
          )}

          {/* TAB 4: MAPPED HOTKEY REGISTRATION CONSOLE */}
          {activeTab === 'HOTKEYS' && (
            <motion.div 
              key="hotkeys"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className="absolute inset-0 p-6 overflow-y-auto"
            >
              <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
                
                {/* Left block: Registration Form */}
                <div className="lg:col-span-7 flex flex-col space-y-6">
                  
                  <div className="bg-[#03010b] border border-violet-900/40 rounded-xl p-5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 blur-[50px] rounded-full pointer-events-none"></div>

                    <div className="mb-4">
                      <h4 className="text-xs font-black text-violet-400 uppercase tracking-widest flex items-center gap-1.5 mb-1.5">
                        <Keyboard className="w-4 h-4 text-violet-400 animate-pulse" />
                        HOTKEY OVERWATCH MATRIX CONFIGURATOR
                      </h4>
                      <p className="text-[9px] text-zinc-400 uppercase tracking-wider leading-relaxed">
                        Customize keyboard triggers for all system commands. Binds save instantly to local storage profile configurations.
                      </p>
                    </div>

                    <div className="space-y-4">
                      {/* ACTION SELECTION */}
                      <div>
                        <label className="block text-[8px] font-mono text-zinc-500 uppercase tracking-widest mb-1.5 font-bold">
                          Select System Target Command
                        </label>
                        <select
                          value={hkAction}
                          onChange={(e) => {
                            const val = e.target.value;
                            setHkAction(val);
                            const selectedOpt = ACTION_OPTIONS.find(o => o.action === val);
                            if (selectedOpt) {
                              setHkName(selectedOpt.name);
                              setHkDescription(selectedOpt.desc);
                            }
                          }}
                          className="w-full bg-[#05030e] border border-violet-900/50 p-2.5 rounded text-[10px] text-white font-bold tracking-wider uppercase focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20"
                        >
                          {ACTION_OPTIONS.map(opt => (
                            <option key={opt.action} value={opt.action} className="bg-[#020108] text-white select">
                              {opt.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* DISPLAY OF METADATA */}
                      <div className="bg-[#05030f]/60 border border-violet-950/40 p-3 rounded">
                        <span className="text-[7px] font-mono text-violet-400 uppercase tracking-widest block font-bold mb-1">
                          Action Meta Description
                        </span>
                        <p className="text-[9.5px] text-zinc-400 uppercase tracking-wider leading-normal">
                          {hkDescription}
                        </p>
                      </div>

                      {/* RECORDING SYSTEM KEY */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[8px] font-mono text-zinc-500 uppercase tracking-widest mb-1.5 font-bold">
                            Listen / Record Stroke
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              setIsRecordingHk(true);
                              triggerSoundTone(650, 750, 0.1);
                            }}
                            className={`w-full py-3 rounded text-[9px] font-extrabold uppercase tracking-widest transition-all p-2 flex items-center justify-center gap-2 border ${
                              isRecordingHk
                                ? 'bg-fuchsia-950/45 text-fuchsia-300 border-fuchsia-500 animate-pulse'
                                : 'bg-violet-950/40 text-violet-300 border-violet-900/60 hover:border-violet-500 hover:text-white'
                            }`}
                          >
                            {isRecordingHk ? (
                              <span className="flex items-center gap-1.5 font-mono text-fuchsia-400">
                                <span className="w-1.5 h-1.5 bg-fuchsia-500 rounded-full animate-ping"></span>
                                RECORDING... PRESS KEY COMBO
                              </span>
                            ) : (
                              <span className="flex items-center gap-1.5 font-sans">
                                <Plus className="w-3.5 h-3.5 text-violet-400" />
                                RECORD PHYSICAL STROKE
                              </span>
                            )}
                          </button>
                        </div>

                        <div>
                          <label className="block text-[8px] font-mono text-zinc-500 uppercase tracking-widest mb-1.5 font-bold">
                            Direct Key Symbol Entry
                          </label>
                          <input
                            type="text"
                            value={hkKey}
                            onChange={(e) => setHkKey(e.target.value)}
                            placeholder="e.g. m, ArrowRight, Enter"
                            className="w-full bg-[#05030e] border border-violet-900/50 p-2.5 rounded text-[10px] text-white font-bold tracking-wider focus:outline-none focus:border-violet-500"
                          />
                        </div>
                      </div>

                      {/* MANUAL MODIFIER CONFIG SWITCHES */}
                      <div>
                        <span className="block text-[8px] font-mono text-zinc-500 uppercase tracking-widest mb-2 font-bold">
                          Fine-Tune Keystroke Modifier Keys
                        </span>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <label className="flex items-center gap-2 bg-[#05030e] border border-violet-950 p-2 rounded cursor-pointer select-none hover:border-violet-900/50">
                            <input
                              type="checkbox"
                              checked={hkCtrl}
                              onChange={(e) => setHkCtrl(e.target.checked)}
                              className="accent-violet-600 rounded"
                            />
                            <span className="text-[8px] text-zinc-300 font-bold uppercase font-mono tracking-wider">Ctrl</span>
                          </label>

                          <label className="flex items-center gap-2 bg-[#05030e] border border-violet-950 p-2 rounded cursor-pointer select-none hover:border-violet-900/50">
                            <input
                              type="checkbox"
                              checked={hkAlt}
                              onChange={(e) => setHkAlt(e.target.checked)}
                              className="accent-violet-600 rounded"
                            />
                            <span className="text-[8px] text-zinc-300 font-bold uppercase font-mono tracking-wider">Alt</span>
                          </label>

                          <label className="flex items-center gap-2 bg-[#05030e] border border-violet-950 p-2 rounded cursor-pointer select-none hover:border-violet-900/50">
                            <input
                              type="checkbox"
                              checked={hkShift}
                              onChange={(e) => setHkShift(e.target.checked)}
                              className="accent-violet-600 rounded"
                            />
                            <span className="text-[8px] text-zinc-300 font-bold uppercase font-mono tracking-wider">Shift</span>
                          </label>

                          <label className="flex items-center gap-2 bg-[#05030e] border border-violet-950 p-2 rounded cursor-pointer select-none hover:border-violet-900/50">
                            <input
                              type="checkbox"
                              checked={hkMeta}
                              onChange={(e) => setHkMeta(e.target.checked)}
                              className="accent-violet-600 rounded"
                            />
                            <span className="text-[8px] text-zinc-300 font-bold uppercase font-mono tracking-wider">Win/Cmd</span>
                          </label>
                        </div>
                      </div>

                      {/* KEY DISPLAY INDICATOR */}
                      <div className="bg-[#04010a] border border-violet-900/40 p-3.5 rounded-xl flex items-center justify-between">
                        <div>
                          <span className="text-[7.5px] font-mono text-zinc-500 uppercase tracking-widest block font-bold">
                            Current Active Mapped Keystroke
                          </span>
                          <span className="text-[14px] text-white font-extrabold uppercase font-mono mt-1 block tracking-wider">
                            {getPrettyKeyString({ key: hkKey, ctrlKey: hkCtrl, altKey: hkAlt, shiftKey: hkShift, metaKey: hkMeta })}
                          </span>
                        </div>
                        <div className="px-3 py-1.5 bg-violet-950/20 border border-violet-500/30 rounded text-right">
                          <span className="text-[7px] text-violet-400 uppercase tracking-wider font-mono font-black block">BIND STABILIZED</span>
                          <span className="text-[8px] text-emerald-400 font-bold font-mono">0.8s HANDSHAKE READY</span>
                        </div>
                      </div>

                      {/* CONFLICT WARNING BANNER */}
                      {getConflictHk() && (
                        <div className="bg-amber-950/40 border border-amber-500/50 p-3.5 rounded text-amber-300 relative overflow-hidden animate-pulse">
                          <div className="flex items-start gap-2.5">
                            <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                            <div>
                              <h5 className="text-[9.5px] font-extrabold uppercase tracking-widest text-amber-300">
                                COLLISION DETECTED
                              </h5>
                              <p className="text-[8.5px] text-slate-300 uppercase tracking-wider mt-1 leading-normal">
                                Warning: The key combination <strong className="text-amber-300 font-black">"{getPrettyKeyString({ key: hkKey, ctrlKey: hkCtrl, altKey: hkAlt, shiftKey: hkShift, metaKey: hkMeta })}"</strong> is already bound to <strong className="text-white">"{getConflictHk()?.name}"</strong>. Registering this will overwrite or conflict with that function!
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* SUBMIT REGISTER */}
                      <button
                        type="button"
                        onClick={handleRegisterHk}
                        className="w-full py-3.5 bg-violet-900 text-white hover:bg-violet-800 transition-colors rounded text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-[0_4px_20px_rgba(139,92,246,0.3)]"
                      >
                        <CheckCircle className="w-4 h-4 text-white" />
                        REGISTER SYSTEM HOTKEY BINDING
                      </button>

                    </div>
                  </div>

                </div>

                {/* Right block: Profile & Saved mappings */}
                <div className="lg:col-span-5 flex flex-col space-y-6">
                  
                  <div className="bg-[#03010b] border border-violet-900/40 rounded-xl p-5 relative overflow-hidden flex-1 flex flex-col min-h-[480px]">
                    <div className="flex justify-between items-center border-b border-violet-950/50 pb-3 mb-4 shrink-0">
                      <div>
                        <h4 className="text-xs font-black text-violet-300 uppercase tracking-widest">
                          SYSTEM HOTKEY DIRECTORY
                        </h4>
                        <span className="text-[7.5px] text-zinc-500 font-mono uppercase tracking-wider block mt-0.5">
                          ACTIVE PROFILE LEDGER OVERWATCH
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleResetDefaults}
                        className="px-2 py-1 bg-red-950/40 border border-red-900/40 text-red-500 hover:border-red-500 hover:text-red-300 transition-colors rounded text-[8.5px] font-bold uppercase tracking-wider"
                      >
                         RESET ALL
                      </button>
                    </div>

                    {/* Mappings scroll container */}
                    <div className="flex-1 overflow-y-auto space-y-2.5 pr-1 max-h-[400px]">
                      {getMergedHotkeys().map(hk => {
                        const isConflict = getMergedHotkeys().some(o => 
                          o.action !== hk.action && 
                          o.key.toLowerCase() === hk.key.toLowerCase() &&
                          o.ctrlKey === hk.ctrlKey &&
                          o.altKey === hk.altKey &&
                          o.shiftKey === hk.shiftKey &&
                          o.metaKey === hk.metaKey
                        );

                        return (
                          <div 
                            key={hk.action}
                            className={`p-3 rounded border transition-colors relative group text-left ${
                              isConflict 
                                ? 'bg-amber-950/20 border-amber-500/30' 
                                : 'bg-[#04010a] border-violet-950/50 hover:border-violet-800/40'
                            }`}
                          >
                            <div className="flex justify-between items-start gap-2 mb-1">
                              <div>
                                <h5 className="text-[9px] font-extrabold text-[#00ffff] uppercase tracking-wider">
                                  {hk.name}
                                </h5>
                                <span className="text-[7px] text-zinc-500 font-mono uppercase tracking-wide">
                                  ACTION: {hk.action}
                                </span>
                              </div>
                              
                              <div className="flex items-center gap-1.5 shrink-0">
                                {isConflict && (
                                  <span className="text-[6.5px] font-bold text-amber-400 bg-amber-950/60 px-1 py-0.5 rounded border border-amber-500/20 animate-pulse">
                                    COLLISION
                                  </span>
                                )}
                                <span className="text-[9px] font-mono text-zinc-300 px-1.5 py-0.5 bg-violet-950/50 border border-violet-900/50 rounded font-black max-w-[120px] truncate uppercase">
                                  {getPrettyKeyString(hk)}
                                </span>
                              </div>
                            </div>
                            
                            <p className="text-[8.5px] text-zinc-400 uppercase tracking-wide leading-relaxed line-clamp-1 group-hover:line-clamp-none transition-all pr-8">
                              {hk.description || 'No override description supplied.'}
                            </p>

                            {/* DELETE / OVERWRITE CUSTOM ROW FLUSH BUTTON */}
                            {hotkeyManagerList.some(o => o.action === hk.action) && (
                              <button
                                type="button"
                                onClick={() => handleDeleteHotkey(hk.action)}
                                title="Reset trigger binding to factory default"
                                className="absolute right-2 bottom-2 text-zinc-650 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 p-1"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-4 pt-3 border-t border-violet-950/50 shrink-0">
                      <div className="bg-[#05030f]/60 border border-violet-950/60 p-2.5 rounded text-[8px] font-mono text-violet-400 uppercase tracking-wider text-center flex items-center justify-center gap-1.5 leading-relaxed">
                        <Activity className="w-3.5 h-3.5 animate-pulse shrink-0" />
                        <span>INTERACTIVE OVERLAY SYSTEM TELEMETRY SYNCED VIA LOCALSCHEMA PROFILE</span>
                      </div>
                    </div>
                  </div>

                </div>

              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      {/* Dynamic CINEMATIC OVERLAY & DYNAMIC GRAPHICS CONTROLLERS */}
      <div className="border-t border-violet-900/40 bg-black/95 p-5 flex flex-col xl:flex-row items-center justify-between gap-6 z-10 relative">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded bg-indigo-950/50 border border-indigo-500/30 flex items-center justify-center shadow-[0_0_10px_rgba(99,102,241,0.2)]">
            <SlidersHorizontal className="w-4 h-4 text-indigo-400" />
          </div>
          <div className="text-left">
            <h3 className="text-[10px] font-black text-indigo-300 uppercase tracking-widest flex items-center gap-1.5">
              <span>CINEMATIC OS OVERLAY CONTROLLER</span>
              <span className="text-[7.5px] bg-emerald-950/40 border border-emerald-500/30 text-emerald-400 px-1 py-0.2 rounded font-bold animate-pulse">ACTIVE</span>
            </h3>
            <p className="text-[8.5px] text-slate-400 uppercase tracking-wider mt-0.5">Quick-click brightness presets, micro blur stabilization & video cycle</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-5">
          {/* BRIGHTNESS PRESETS */}
          <div className="flex flex-col items-start gap-1">
            <span className="text-[7.5px] text-slate-500 font-extrabold uppercase tracking-widest font-mono">
              Background Brightness
            </span>
            <div className="flex bg-[#05030f] p-1 border border-violet-900/40 rounded-lg">
               {[
                { label: 'STEALTH (3%)', value: 0.03 },
                { label: 'MUTED (8%)', value: 0.08 },
                { label: 'BALANCED (18%)', value: 0.18 }
              ].map((preset) => {
                const isActive = Math.abs(localBrightness - preset.value) < 0.025;
                return (
                  <button
                    key={preset.label}
                    onClick={() => {
                      setLocalBrightness(preset.value);
                      window.dispatchEvent(new CustomEvent('nexorax-video-brightness', { detail: preset.value }));
                      triggerSoundTone(500, preset.value * 250 + 200, 0.08);
                    }}
                    className={`px-3 py-1 text-[8.5px] font-bold uppercase transition-all rounded cursor-pointer ${
                      isActive 
                        ? 'bg-indigo-900/50 text-indigo-300 border border-indigo-500/40 shadow'
                        : 'text-indigo-500/50 hover:text-indigo-400 border border-transparent'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="h-8 w-[1px] bg-violet-900/30 hidden sm:block"></div>

          {/* DUAL-LAYER OPACITY CONTROLLERS */}
          <div className="flex flex-col items-start gap-1">
            <span className="text-[7.5px] text-slate-500 font-extrabold uppercase tracking-widest font-mono">
              Background Opacity
            </span>
            <div className="flex bg-[#05030f] p-1 border border-violet-900/40 rounded-lg">
              {[
                { label: 'DIM 25%', value: 0.25 },
                { label: 'DIM 50%', value: 0.50 },
                { label: 'DIM 75%', value: 0.75 }
              ].map((preset) => {
                const isActive = Math.abs(localOpacity - preset.value) < 0.05;
                return (
                  <button
                    key={preset.label}
                    onClick={() => {
                      setLocalOpacity(preset.value);
                      window.dispatchEvent(new CustomEvent('nexorax-video-opacity', { detail: preset.value }));
                      triggerSoundTone(450, preset.value * 200 + 200, 0.08);
                    }}
                    className={`px-2.5 py-1 text-[8px] font-bold uppercase transition-all rounded cursor-pointer ${
                      isActive 
                        ? 'bg-indigo-900/50 text-cyan-300 border border-indigo-500/40 shadow-[0_0_8px_rgba(34,211,238,0.2)]'
                        : 'text-indigo-500/50 hover:text-indigo-400 border border-transparent'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="h-8 w-[1px] bg-violet-900/30 hidden sm:block"></div>

          {/* AMBIENT BLUR TOGGLE */}
          <div className="flex flex-col items-start gap-1">
            <span className="text-[7.5px] text-slate-500 font-extrabold uppercase tracking-widest font-mono">
              Ambient Blur
            </span>
            <button
              onClick={() => {
                const updated = !localBlur;
                setLocalBlur(updated);
                window.dispatchEvent(new CustomEvent('nexorax-video-blur', { detail: updated }));
                triggerSoundTone(updated ? 700 : 400, updated ? 900 : 250, 0.1);
              }}
              className={`px-4 py-2 text-[8.5px] font-black uppercase transition-all rounded-lg border flex items-center gap-2 cursor-pointer ${
                localBlur
                  ? 'bg-fuchsia-950/40 text-fuchsia-300 border-fuchsia-500/40 shadow-[0_0_10px_rgba(217,70,239,0.25)]'
                  : 'bg-black/40 text-slate-500 border-violet-900/30 hover:border-violet-500/30 hover:text-slate-300'
              }`}
            >
              <div className={`w-1.5 h-1.5 rounded-full transition-colors ${localBlur ? 'bg-fuchsia-400 animate-ping' : 'bg-slate-600'}`}></div>
              <span>{localBlur ? 'BLUR ACTIVE' : 'BLUR INACTIVE'}</span>
            </button>
          </div>

          <div className="h-8 w-[1px] bg-violet-900/30 hidden sm:block"></div>

          {/* AUTO-PLAY TOGGLE */}
          <div className="flex flex-col items-start gap-1 font-mono">
            <span className="text-[7.5px] text-slate-500 font-extrabold uppercase tracking-widest font-mono">
              Neural Auto-Cycle
            </span>
            <button
              id="video-autocycle-btn"
              onClick={() => {
                const updated = !localAutoplay;
                setLocalAutoplay(updated);
                window.dispatchEvent(new CustomEvent('nexorax-video-autoplay', { detail: updated }));
                triggerSoundTone(updated ? 750 : 355, updated ? 500 : 300, 0.08);
              }}
              className={`px-4 py-2 text-[8.5px] font-black uppercase transition-all rounded-lg border flex items-center gap-2 cursor-pointer ${
                localAutoplay
                  ? 'bg-cyan-950/40 text-cyan-300 border-cyan-500/40 shadow-[0_0_10px_rgba(6,182,212,0.25)]'
                  : 'bg-black/40 text-slate-500 border-violet-900/30 hover:border-violet-500/30 hover:text-slate-300'
              }`}
            >
              <div className={`w-1.5 h-1.5 rounded-full transition-colors ${localAutoplay ? 'bg-cyan-400 animate-pulse' : 'bg-slate-600'}`}></div>
              <span>{localAutoplay ? 'AUTO-PLAY ON' : 'AUTO-PLAY OFF'}</span>
            </button>
          </div>

          <div className="h-8 w-[1px] bg-violet-900/30 hidden sm:block"></div>

          {/* AUTO-DIM TOGGLE */}
          <div className="flex flex-col items-start gap-1 font-mono">
            <span className="text-[7.5px] text-slate-500 font-extrabold uppercase tracking-widest font-mono">
              Auto-Dim Response
            </span>
            <button
              id="video-autodim-btn"
              onClick={() => {
                const updated = !localAutoDim;
                setLocalAutoDim(updated);
                window.dispatchEvent(new CustomEvent('nexorax-video-autodim-toggle', { detail: updated }));
                localStorage.setItem('nexorax_video_autodim', String(updated));
                triggerSoundTone(updated ? 650 : 380, updated ? 500 : 300, 0.08);
              }}
              className={`px-4 py-2 text-[8.5px] font-black uppercase transition-all rounded-lg border flex items-center gap-2 cursor-pointer ${
                localAutoDim
                  ? 'bg-violet-950/40 text-violet-300 border-violet-500/40 shadow-[0_0_10px_rgba(139,92,246,0.25)]'
                  : 'bg-black/40 text-slate-500 border-violet-900/30 hover:border-violet-500/30 hover:text-slate-300'
              }`}
            >
              <div className={`w-1.5 h-1.5 rounded-full transition-colors ${localAutoDim ? 'bg-violet-400 animate-pulse' : 'bg-slate-600'}`}></div>
              <span>{localAutoDim ? 'AUTO-DIM ACTIVE' : 'AUTO-DIM INACTIVE'}</span>
            </button>
          </div>

          <div className="h-8 w-[1px] bg-violet-900/30 hidden sm:block"></div>

          {/* PLAY / PAUSE CONTROLLER */}
          <div className="flex flex-col items-start gap-1 font-mono">
            <span className="text-[7.5px] text-slate-500 font-extrabold uppercase tracking-widest font-mono">
              Playback State
            </span>
            <button
              id="video-playpause-btn"
              onClick={() => {
                const updated = !localPaused;
                setLocalPaused(updated);
                window.dispatchEvent(new CustomEvent('nexorax-video-playpause', { detail: updated }));
                triggerSoundTone(updated ? 350 : 680, updated ? 400 : 720, 0.1);
              }}
              className={`px-4 py-2 text-[8.5px] font-black uppercase transition-all rounded-lg border flex items-center gap-2 cursor-pointer ${
                !localPaused
                  ? 'bg-amber-950/40 text-amber-300 border-amber-500/40 shadow-[0_0_10px_rgba(245,158,11,0.25)]'
                  : 'bg-emerald-950/40 text-emerald-300 border-emerald-500/40 shadow-[0_0_10px_rgba(16,185,129,0.25)]'
              }`}
            >
              {!localPaused ? (
                <>
                  <Pause className="w-3 h-3 text-amber-400" />
                  <span>PAUSE STREAM</span>
                </>
              ) : (
                <>
                  <Play className="w-3 h-3 text-emerald-400 fill-emerald-400" />
                  <span>RESUME STREAM</span>
                </>
              )}
            </button>
          </div>

          <div className="h-8 w-[1px] bg-violet-900/30 hidden sm:block"></div>

          {/* CROSS-FADE TRANSITION SLIDER */}
          <div className="flex flex-col items-start gap-1 font-mono">
            <span className="text-[7.5px] text-slate-500 font-extrabold uppercase tracking-widest font-mono flex items-center gap-1">
              Cross-Fade Transition
              <span className="text-violet-400 font-bold bg-violet-950/40 border border-violet-500/20 px-1 py-0.2 rounded text-[7px]">
                {localTransitionDuration}ms
              </span>
            </span>
            <div className="flex items-center gap-2 bg-[#05030f] px-3 py-1.5 border border-violet-900/40 rounded-lg h-[34px] w-[140px] sm:w-[170px]">
              <input
                type="range"
                min="100"
                max="3000"
                step="100"
                value={localTransitionDuration}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setLocalTransitionDuration(val);
                  window.dispatchEvent(new CustomEvent('nexorax-video-transition-duration', { detail: val }));
                  triggerSoundTone(100 + val / 15, 60, 0.05);
                }}
                className="w-full accent-indigo-500 cursor-pointer h-1 bg-violet-950/40 rounded-lg outline-none appearance-none"
              />
            </div>
          </div>

          <div className="h-8 w-[1px] bg-violet-900/30 hidden sm:block"></div>

          {/* BACKGROUND VIDEO CYCLER */}
          <div className="flex flex-col items-start gap-1">
            <span className="text-[7.5px] text-slate-500 font-extrabold uppercase tracking-widest font-mono">
              Active Neural Stream
            </span>
            <button
              onClick={() => {
                const nextIndex = (localVideoIndex + 1) % bgVideos.length;
                setLocalVideoIndex(nextIndex);
                window.dispatchEvent(new CustomEvent('nexorax-video-cycle', { detail: bgVideos[nextIndex] }));
                triggerSoundTone(650, 950, 0.12);
              }}
              className="px-4 py-2 text-[8.5px] font-black bg-indigo-950/10 hover:bg-indigo-950/30 border border-indigo-500/30 text-indigo-300 hover:text-white rounded-lg flex items-center gap-2 cursor-pointer transition-all active:scale-[0.98]"
            >
              <RefreshCw className="w-3.5 h-3.5 text-indigo-400 rotate-180" />
              <span>FEED: {localVideoIndex + 1} / {bgVideos.length}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
